import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import api, { getApiErrorMessage } from "../services/api";
import clientConfigService from "../services/clientConfig";
import { saveDraftToStorage, loadDraftFromStorage, removeDraftFromStorage } from "../utils/storage";
import { pruneHiddenQuestionResponses } from "./questionnaireVisibility";
import { buildOrderQuestionnaireAnswers } from "../utils/questionnaireResponses";
import { logoOnlyBrandingOverride } from "../utils/questionnaireBranding";
import {
  prepareTreatmentCheckout,
  continueWithEligiblePrograms as continueWithEligibleProgramsAuthority,
  startTreatmentPreviewAuthority,
  synchronizeProgramCheckpointBoundary,
  synchronizeQuestionBoundary,
  synchronizeTreatmentAuthority as synchronizeAuthoritySession,
} from "../features/treatment-flow/authorityCoordinator";
import {
  bindTreatmentPatient,
  getTreatmentSession,
  recoverTreatmentCheckout,
  removeTreatmentRuntimeFile as removeRuntimeFile,
  submitTreatmentCheckout,
  transitionTreatmentSession,
  validateTreatmentSessionLocation,
  uploadTreatmentRuntimeFile as uploadRuntimeFile,
} from "../features/treatment-flow/authorityApi";
import {
  TREATMENT_RECOVERY_ACTION,
  TREATMENT_RUNTIME_ACTION,
  TREATMENT_RUNTIME_STATE,
  TREATMENT_RUNTIME_STATE_ORDER,
  TREATMENT_STORAGE_KEY,
  TREATMENT_SOURCE_KIND,
} from "../features/treatment-flow/treatmentApiConstants";
import {
  authorityResponses,
  reconcileAuthorityResponses,
  clearTreatmentAuthority,
  loadTreatmentAuthority,
  mergeAuthorityResponses,
  persistTreatmentAuthority,
  persistTreatmentCheckoutResume,
} from "../features/treatment-flow/authorityResume";
import {
  authorityStateForQuestion,
} from "../features/treatment-flow/authorityProgress";
import {
  clearTreatmentPreviewSession,
  loadTreatmentPreviewSession,
  persistTreatmentPreviewSession,
} from "../features/treatment-flow/previewSessionStorage";
import {
  buildBoundaryAnswerCheckpoint,
  buildProgramAnswerCheckpoint,
  checkpointProgramIdentity,
  buildProgramAnswerContext,
  buildAuthorityPayload,
  canonicalLocationAnswer,
  hasCanonicalProgramDisqualifier,
  requiresProgramAnswerCheckpoint,
  recoverHiddenAnswerCheckpoint,
  normalizePaymentMethod,
  paymentMethodToken,
} from "../features/treatment-flow/authorityPayload";
import {
  createTreatmentAuthorityMutationGate,
} from "../features/treatment-flow/authorityMutationGate";
import {
  buildLiveOrderSummary,
  previewPatientDefaults,
} from "../features/treatment-flow/previewSimulation";
import { createJourneyBoundary } from "../features/treatment-flow/journeyBoundary";
import {
  JOURNEY_PREVIEW_ERROR,
  JOURNEY_PROTECTED_EFFECT,
} from "../features/treatment-flow/journeyConstants";
import { normalizeProgramLabCheckout } from "../features/treatment-flow/programLabCheckout";
import {
  buildQuestionIdentityMap,
} from "../features/treatment-flow/questionIdentity";
import {
  questionIsBeforeRuntimeBoundary,
  orderQuestionsByRuntimeBoundary,
} from "../features/treatment-flow/runtimeBoundaryNavigation";
import {
  isTreatmentQuestionVisible,
  pruneNewlyHiddenProgramResponses,
} from "../features/treatment-flow/questionVisibility";
import {
  hasVisibleProgramLabs,
} from "../features/treatment-flow/programLabVisibility";
import {
  evaluateQuestionDisqualification,
  shouldEvaluateDisqualificationLocally,
} from "../features/treatment-flow/questionDisqualification";
import {
  resolveQuestionnaireRuntimeAdapter,
} from "../features/treatment-flow/runtimeAdapter";
import {
  restoreTreatmentRuntimeDraft,
  treatmentRuntimeDraftIdentity,
  treatmentRuntimeDraftKey,
} from "../features/treatment-flow/runtimeDraft";
import {
  assertCombinedCheckoutSuccess,
  isCombinedCheckoutSuccessful,
  projectRuntimeQuestionnaireQuestions,
} from "../features/treatment-flow/checkoutContract";
import {
  checkoutRecoveryForError,
  ensureCheckoutClientIdempotencyKey,
  restoreCheckoutClientIdempotencyKey,
} from "../features/treatment-flow/phase2CheckoutState";
import { getUsPhoneState } from "../services/usPhoneNumber";
import {
  isEmptyQuestionResponse,
  isQuestionRequired,
  requiredGroupedFieldError,
} from "../utils/questionValidation";
import { validateUploadFile } from "../utils/fileUploadConfig";
import {
  authorityCursorRequiresNavigation,
  firstVisibleNodeIndexForState,
  resolveAuthoritativeNodeIndex,
  resolveForwardNodeIndex,
  runtimePointerNodeId,
  runtimeResumeCursor,
  resolveStableNodeIndex,
  shouldPreservePreviewAuthenticationCursor,
} from "../features/treatment-flow/stableNodeNavigation";
import {
  reconcileTreatmentSession,
} from "../features/treatment-flow/browserCacheReconciliation";
import { runtimeDisqualificationFromProjection } from "../features/treatment-flow/runtimeDisqualification";
import {
  closeTreatmentRuntimeChannel,
  createTreatmentRuntimeChannel,
  isNewerTreatmentRuntimeProjection,
  treatmentRuntimeRevision,
} from "../features/treatment-flow/runtimeTabCoordination";
import {
  normalizeRuntimeAuthority,
  questionIsInCurrentRuntimeStep,
} from "../features/treatment-flow/runtimeAuthority";

const sanitizeDraftResponses = (responsesToSanitize) => {
  if (!responsesToSanitize || typeof responsesToSanitize !== "object") {
    return responsesToSanitize;
  }
  const sanitized = {};
  for (const [key, value] of Object.entries(responsesToSanitize)) {
    if (
      value
      && typeof value === "object"
      && !Array.isArray(value)
      && typeof value.fileData === "string"
      && value.fileData.startsWith("data:")
    ) {
      sanitized[key] = {
        filename: value.filename,
        fileType: value.fileType,
        fileSize: value.fileSize,
        uploadedAt: value.uploadedAt,
        lastModified: value.lastModified,
        hasFileData: true,
      };
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

const PATIENT_INFO_MAPPINGS = [
  "first_name",
  "last_name",
  "date_of_birth",
  "phone_number",
  "email",
  "street_address",
  "address2",
  "address_line_2",
  "city",
  "state",
  "zip_code",
];

const inferPatientInfoMapping = (question) => {
  if (!question) return null;
  if (PATIENT_INFO_MAPPINGS.includes(question.beluga_field_mapping)) {
    return question.beluga_field_mapping;
  }
  const text = (question.question_text || "").toLowerCase();
  if (question.question_type === "email" || text.includes("email")) return "email";
  if (question.question_type === "phone" || text.includes("phone")) return "phone_number";
  if (question.question_type === "state") return "state";
  if (question.question_type === "zip") return "zip_code";
  if (question.question_type === "date" && (text.includes("date of birth") || text.trim() === "dob")) {
    return "date_of_birth";
  }
  if (text.includes("first name")) return "first_name";
  if (text.includes("last name")) return "last_name";
  if (text.includes("date of birth") || text.trim() === "dob") return "date_of_birth";
  if (
    /\b(apartment|suite|apt\.?)\b/.test(text) ||
    text.includes("address line 2") ||
    text.includes("address 2") ||
    (/\bunit\b/.test(text) && /\b(address|apartment|apt|suite|street|mailing|shipping|home)\b/.test(text))
  ) {
    return "address2";
  }
  if (text.includes("street address")) return "street_address";
  if (text.includes("city")) return "city";
  if (text.includes("zip")) return "zip_code";
  return null;
};

const hasRuleContent = (rules) =>
  Boolean(rules && typeof rules === "object" && Object.keys(rules).length > 0);

const firstRuleSet = (...candidates) =>
  candidates.find((rules) => hasRuleContent(rules)) || {};

export const useFrontendQuestionnaire = (
  templateId,
  patientId = null,
  options = {}
) => {
  const prefillSources = options?.prefillSources || {};
  const disableLocalDraft = options?.disableLocalDraft || false;
  const previewProgramId = options?.previewProgramId || null;
  const previewApiBaseUrl = options?.previewApiBaseUrl || null;
  // Published runtime URLs are authoritative. Keep a final fallback here so
  // an older/stale parent render cannot silently initialize an empty legacy
  // questionnaire when the URL already identifies a live Program.
  const launchUrlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const runtimeProgramId = (
    options?.runtimeProgramId
    || launchUrlParams.get("program_id")
    || null
  );
  const runtimeCustomProgramId = (
    options?.runtimeCustomProgramId
    || launchUrlParams.get("custom_program_id")
    || null
  );
  const runtimeApiBaseUrl = options?.runtimeApiBaseUrl || null;
  const runtimeSourceKind = runtimeProgramId
    ? TREATMENT_SOURCE_KIND.program
    : runtimeCustomProgramId
      ? TREATMENT_SOURCE_KIND.customProgram
      : null;
  const runtimeSourceId = runtimeProgramId || runtimeCustomProgramId || null;
  const previewCustomProgramId = options?.previewCustomProgramId || null;
  const previewCapabilityToken = options?.previewCapabilityToken || null;
  const previewSnapshotChecksum = options?.previewSnapshotChecksum || null;
  const previewSnapshotId = options?.previewSnapshotId || null;
  const runtimeAdapter = useMemo(
    () => resolveQuestionnaireRuntimeAdapter({
      templateId,
      runtimeProgramId,
      runtimeCustomProgramId,
      runtimeApiBaseUrl,
      previewProgramId,
      previewCustomProgramId,
      previewCapabilityToken,
      previewSnapshotChecksum,
      previewSnapshotId,
    }),
    [
      previewCapabilityToken,
      previewCustomProgramId,
      previewProgramId,
      previewSnapshotChecksum,
      previewSnapshotId,
      runtimeCustomProgramId,
      runtimeApiBaseUrl,
      runtimeProgramId,
      templateId,
    ],
  );
  const hasTreatmentAuthority = runtimeAdapter.hasAuthority;
  const journeyBoundary = useMemo(
    () => createJourneyBoundary({ previewCapabilityToken }),
    [previewCapabilityToken],
  );
  const isPreviewMode = journeyBoundary.isPreview;
  const [questionnaire, setQuestionnaire] = useState(null);
  const [responses, setResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isRehydrating, setIsRehydrating] = useState(true);
  const [error, setError] = useState(null);
  const [treatmentRuntimeError, setTreatmentRuntimeError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [sessionStartTime] = useState(Date.now());

  const [validationErrors, setValidationErrors] = useState({});
  const [disqualified, setDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState(null);
  const [questionIdMap, setQuestionIdMap] = useState({});
  const [calculatedBMI, setCalculatedBMI] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [treatmentCheckout, setTreatmentCheckout] = useState(null);
  const [treatmentCheckoutRecovery, setTreatmentCheckoutRecovery] = useState(null);
  const [treatmentRecoveryMessage, setTreatmentRecoveryMessage] = useState("");
  const [treatmentQuoteChange, setTreatmentQuoteChange] = useState(null);
  /** Runtime tenant branding is intentionally limited to its logo media. */
  const [questionnaireBranding, setQuestionnaireBranding] = useState(null);

  // Add debounce timer ref for weight input
  const weightDebounceTimer = useRef(null);
  const dobDebounceTimer = useRef(null);

  // Ref that always holds the latest committed responses, so closures inside
  // setTimeout (auto-advance) never read stale data.
  const responsesRef = useRef(responses);
  useEffect(() => { responsesRef.current = responses; }, [responses]);
  // The response handler needs to know synchronously whether this update
  // triggered local Custom Program disqualification. React state updates are
  // asynchronous, so reading `disqualified` immediately after updateResponse
  // would otherwise still return the previous value.
  const lastResponseDisqualifiedRef = useRef(false);

  // Persistence Refs & Helpers
  const hasRehydrated = useRef(false);
  const saveTimeoutRef = useRef(null);
  const flushDraftToStorageRef = useRef(null);
  const draftSaveQueueRef = useRef(Promise.resolve());
  const queueDraftSave = useCallback((writeDraft) => {
    const queued = draftSaveQueueRef.current
      .catch(() => undefined)
      .then(writeDraft);
    draftSaveQueueRef.current = queued;
    return queued;
  }, []);
  const draftKey = runtimeAdapter.persistenceKey;
  const activeDraftKeyRef = useRef(draftKey);
  const currentToken = useRef(null);
  const questionnaireSessionIdRef = useRef(null);
  const treatmentAuthorityRef = useRef(null);
  const [treatmentAuthority, setTreatmentAuthority] = useState(null);
  const setTreatmentAuthorityRef = useCallback((value) => {
    treatmentAuthorityRef.current = value;
    setTreatmentAuthority(value);
  }, []);
  const updateTreatmentPreviewProfile = useCallback((profile) => {
    const current = treatmentAuthorityRef.current;
    if (
      !current
      || current.mode !== "preview"
      || !profile
      || typeof profile !== "object"
    ) {
      return current;
    }
    const sex = String(profile.sex || "").trim().toLowerCase();
    const dob = profile.date_of_birth || profile.dateOfBirth;
    let age = Number(profile.age);
    if (!Number.isFinite(age) && dob) {
      const birthDate = new Date(`${dob}T00:00:00Z`);
      if (!Number.isNaN(birthDate.getTime())) {
        const today = new Date();
        age = today.getUTCFullYear() - birthDate.getUTCFullYear();
        const beforeBirthday = (
          today.getUTCMonth() < birthDate.getUTCMonth()
          || (
            today.getUTCMonth() === birthDate.getUTCMonth()
            && today.getUTCDate() < birthDate.getUTCDate()
          )
        );
        if (beforeBirthday) age -= 1;
      }
    }
    const patientProfile = {
      ...(sex ? { sex } : {}),
      ...(Number.isFinite(age) ? { age } : {}),
    };
    if (!Object.keys(patientProfile).length) return current;
    const visibilityContext = {
      ...(current.visibility_context || {}),
      patient_profile: patientProfile,
    };
    const updated = {
      ...current,
      visibility_context: visibilityContext,
    };
    setTreatmentAuthorityRef(updated);
    setQuestionnaire((currentQuestionnaire) => (
      currentQuestionnaire
        ? {
            ...currentQuestionnaire,
            runtime_visibility_context: visibilityContext,
          }
        : currentQuestionnaire
    ));
    return updated;
  }, [setTreatmentAuthorityRef]);
  const treatmentRuntimeChannelRef = useRef(null);
  const treatmentRuntimeReconcileRef = useRef(Promise.resolve());
  const treatmentRuntimeReconcileTimerRef = useRef(null);
  const treatmentRuntimeReconcileInFlightRef = useRef(null);
  const treatmentUploadInFlightRef = useRef(0);
  const treatmentAuthorityMutationGateRef = useRef(null);
  if (!treatmentAuthorityMutationGateRef.current) {
    treatmentAuthorityMutationGateRef.current =
      createTreatmentAuthorityMutationGate();
  }
  const treatmentCheckoutRef = useRef(null);
  const treatmentCheckoutAttemptRef = useRef(null);
  const treatmentCheckoutSubmitInFlightRef = useRef(null);
  const resumeCursorRef = useRef(null);
  const pendingStableNavigationRef = useRef(null);
  const [stableNavigationRevision, setStableNavigationRevision] = useState(0);
  const initializationSequenceRef = useRef(0);
  const runtimeIdentityRef = useRef(runtimeAdapter.persistenceKey);

  const initialize = useCallback(async () => {
    const initializationSequence = ++initializationSequenceRef.current;
    const assertCurrentInitialization = () => {
      if (initializationSequence !== initializationSequenceRef.current) {
        const superseded = new Error("Questionnaire initialization was superseded.");
        superseded.code = "questionnaire_initialization_superseded";
        throw superseded;
      }
    };

    setLoading(true);
    setError(null);
    setTreatmentRuntimeError(null);
    if (runtimeIdentityRef.current !== runtimeAdapter.persistenceKey) {
      if (hasTreatmentAuthority) {
        removeDraftFromStorage(activeDraftKeyRef.current);
      }
      runtimeIdentityRef.current = runtimeAdapter.persistenceKey;
      activeDraftKeyRef.current = draftKey;
      hasRehydrated.current = false;
      setIsRehydrating(true);
      currentToken.current = null;
      questionnaireSessionIdRef.current = null;
      setTreatmentAuthorityRef(null);
      treatmentCheckoutRef.current = null;
      treatmentCheckoutAttemptRef.current = null;
      treatmentCheckoutSubmitInFlightRef.current = null;
      resumeCursorRef.current = null;
      pendingStableNavigationRef.current = null;
      responsesRef.current = {};
      setResponses({});
      setCurrentQuestionIndex(0);
      setNavigationHistory([]);
      setPatientInfo(null);
      setCompleted(false);
      setDisqualified(false);
      setDisqualificationReason(null);
      setTreatmentCheckout(null);
      setTreatmentCheckoutRecovery(null);
      setTreatmentQuoteChange(null);
    }

    try {
      let result;
      let authorityResume = null;
      let authoritativeRestored = {};
      if (previewCapabilityToken) {
        result = await clientConfigService.getTreatmentPreviewCapability(
          previewCapabilityToken,
          previewApiBaseUrl
        );
        assertCurrentInitialization();
        const resolvedCapability = result.preview_capability || {};
        if (
          !previewSnapshotId ||
          !previewSnapshotChecksum ||
          resolvedCapability.source_snapshot_id !== previewSnapshotId ||
          resolvedCapability.snapshot_checksum !== previewSnapshotChecksum
        ) {
          const mismatchError = new Error(
            "The preview snapshot changed or does not match this launch. Refresh from the portal."
          );
          mismatchError.code = JOURNEY_PREVIEW_ERROR.snapshotMismatch;
          throw mismatchError;
        }
        const previewQuestionnaire = (
          result.questionnaire_json || result.questionnaire
        );
        const previewAuthority = await startTreatmentPreviewAuthority(
          previewCapabilityToken,
          previewApiBaseUrl,
          {
            snapshotId: previewSnapshotId,
            snapshotChecksum: previewSnapshotChecksum,
            sourceKind: resolvedCapability.source_kind || null,
            sourceId: resolvedCapability.source_id || null,
            previewPatient: previewPatientDefaults(previewQuestionnaire),
          },
        );
        assertCurrentInitialization();
        setTreatmentAuthorityRef(previewAuthority);
        let savedRuntime = null;
        try {
          savedRuntime = loadTreatmentPreviewSession(
            previewCapabilityToken,
            {
              apiBaseUrl: previewApiBaseUrl,
              snapshotId: previewSnapshotId,
              snapshotChecksum: previewSnapshotChecksum,
            },
          );
        } catch (_pointerError) {
          savedRuntime = null;
        }
        authorityResume = {
          resumed: previewAuthority,
          savedRuntime,
        };
      } else if (runtimeAdapter.initializeLegacy) {
        result = await api.initializeFrontendQuestionnaire(
          templateId,
          patientId
        );
        assertCurrentInitialization();
      }

      if (runtimeProgramId) {
        const programResult = await clientConfigService.getProgramRuntime(
          runtimeProgramId,
          runtimeApiBaseUrl,
        );
        assertCurrentInitialization();
        const authority = await loadTreatmentAuthority({
          sourceKind: TREATMENT_SOURCE_KIND.program,
          sourceId: runtimeProgramId,
          release: programResult.release,
          apiBaseUrl: runtimeApiBaseUrl,
          runtimeSessionToken: launchUrlParams.get("runtime_session_token"),
        });
        assertCurrentInitialization();
        authorityResume = authority;
        setTreatmentAuthorityRef(authority.session);
        if (
          authority.resumed?.state === "checkout_ready"
          && authority.resumed?.checkout_capability
        ) {
          const resumedCheckout = restoreCheckoutClientIdempotencyKey(
            authority.resumed,
            authority.savedCheckout,
          );
          treatmentCheckoutRef.current = resumedCheckout;
          treatmentCheckoutAttemptRef.current = (
            resumedCheckout.client_idempotency_key
          );
          persistTreatmentCheckoutResume(resumedCheckout, {
            sourceKind: TREATMENT_SOURCE_KIND.program,
            sourceId: runtimeProgramId,
            idempotencyKey: resumedCheckout.client_idempotency_key,
            recovery: authority.savedCheckout?.recovery || null,
          });
          setTreatmentCheckoutRecovery(
            authority.savedCheckout?.recovery || null,
          );
          setTreatmentCheckout({
            ...resumedCheckout,
            order_summary: buildLiveOrderSummary(resumedCheckout),
          });
        }
        result = {
          ...result,
          questionnaire_json: programResult.questionnaire,
          // The runtime response owns the current tenant brand.  Do not lose
          // it while swapping the generic questionnaire payload for Program.
          questionnaire_branding: (
            programResult.questionnaire_branding
            || result.questionnaire_branding
          ),
          program_id: runtimeProgramId,
          program_release: programResult.release,
        };
      } else if (runtimeCustomProgramId) {
        const customProgramResult = await clientConfigService.getCustomProgramRuntime(
          runtimeCustomProgramId,
          runtimeApiBaseUrl,
        );
        assertCurrentInitialization();
        const runtimeTemplateId = customProgramResult.questionnaire?.id;
        if (!runtimeTemplateId) {
          throw new Error("Custom Program runtime did not provide a questionnaire template.");
        }
        const authority = await loadTreatmentAuthority({
          sourceKind: TREATMENT_SOURCE_KIND.customProgram,
          sourceId: runtimeCustomProgramId,
          release: customProgramResult.release,
          apiBaseUrl: runtimeApiBaseUrl,
        });
        assertCurrentInitialization();
        authorityResume = authority;
        const resumedAuthority = authority.resumed;
        const savedCheckout = authority.savedCheckout;
        setTreatmentAuthorityRef(authority.session);
        if (
          resumedAuthority?.state === "checkout_ready"
          && resumedAuthority?.checkout_capability
        ) {
          const resumedCheckout = restoreCheckoutClientIdempotencyKey(
            resumedAuthority,
            savedCheckout,
          );
          treatmentCheckoutRef.current = resumedCheckout;
          treatmentCheckoutAttemptRef.current = (
            resumedCheckout.client_idempotency_key
          );
          persistTreatmentCheckoutResume(resumedCheckout, {
            sourceKind: TREATMENT_SOURCE_KIND.customProgram,
            sourceId: runtimeCustomProgramId,
            idempotencyKey: resumedCheckout.client_idempotency_key,
            recovery: savedCheckout?.recovery || null,
          });
          setTreatmentCheckoutRecovery(
            savedCheckout?.recovery || null
          );
          setTreatmentCheckout({
            ...resumedCheckout,
            order_summary: buildLiveOrderSummary(resumedCheckout),
          });
        }
        result = {
          ...result,
          questionnaire_json: customProgramResult.questionnaire,
          // Custom Program uses the same runtime branding contract as Program.
          questionnaire_branding: (
            customProgramResult.questionnaire_branding
            || result.questionnaire_branding
          ),
          custom_program_id: runtimeCustomProgramId,
          custom_program_release: customProgramResult.release,
        };
      }

      const rawQuestionnaireData =
        result.questionnaire_json || result.questionnaire;
      const authorityVisibilityContext = (
        authorityResume?.resumed?.visibility_context
        || authorityResume?.session?.visibility_context
        || null
      );
      const questionnaireData = rawQuestionnaireData
        ? {
            ...rawQuestionnaireData,
            ...(authorityVisibilityContext
              ? { runtime_visibility_context: authorityVisibilityContext }
              : {}),
          }
        : rawQuestionnaireData;
      if (
        authorityResume?.resumed?.saved_answers
      ) {
        const restored = authorityResponses(
          questionnaireData,
          authorityResume.resumed,
        );
        authoritativeRestored = restored;
        responsesRef.current = restored;
        setResponses(restored);
      }
      resumeCursorRef.current = runtimeResumeCursor({
        resumed: authorityResume?.resumed,
        savedRuntime: authorityResume?.savedRuntime,
      });
      if (
        authorityResume?.resumed?.state
        === TREATMENT_RUNTIME_STATE.disqualified
      ) {
        setDisqualified(true);
        setDisqualificationReason(runtimeDisqualificationFromProjection({
          projection: authorityResume.resumed,
          questionnaire: questionnaireData,
        }));
      }
      if (
        authorityResume?.resumed?.state
        === TREATMENT_RUNTIME_STATE.completed
      ) {
        setCompleted(true);
      }
      // Validate the complete immutable identity graph before installing any
      // questionnaire/UI state. Previously the form was committed first and
      // identity validation threw afterward, exposing a fatal banner above a
      // partially usable checkpoint and letting Retry rebase that stale form.
      const idMap = buildQuestionIdentityMap(questionnaireData.questions);
      setQuestionnaire(questionnaireData);
      setQuestionIdMap(idMap);
      setQuestionStartTime(Date.now());
      currentToken.current = result.session_token || null;
      questionnaireSessionIdRef.current = result.session_id || null;

      const qb = result.questionnaire_branding;
      setQuestionnaireBranding(logoOnlyBrandingOverride(qb));

      if (hasTreatmentAuthority && !isPreviewMode) {
        const runtimeSession = authorityResume?.session;
        activeDraftKeyRef.current = treatmentRuntimeDraftKey(
          draftKey,
          runtimeSession?.session_token,
        );
        // Program-only draft keys predate session isolation and may contain
        // another patient's answers. They are never eligible for migration.
        removeDraftFromStorage(draftKey);
      }

      // --- REHYDRATION GATE ---
      if (!isPreviewMode && !hasRehydrated.current) {
        let draft = null;

        if (hasTreatmentAuthority && !disableLocalDraft) {
          const runtimeSession = authorityResume?.session;
          const stored = await loadDraftFromStorage(activeDraftKeyRef.current);
          const expectedIdentity = treatmentRuntimeDraftIdentity({
            session: runtimeSession,
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
            apiBaseUrl: runtimeApiBaseUrl,
          });
          draft = restoreTreatmentRuntimeDraft({
            draft: stored,
            expectedIdentity,
            questionnaire: questionnaireData,
            authoritativeResponses: authoritativeRestored,
          });
          if (stored && !draft) {
            removeDraftFromStorage(activeDraftKeyRef.current);
          }
        }
        else if (hasTreatmentAuthority) {
          removeDraftFromStorage(activeDraftKeyRef.current);
        }

        // 1. Check API returned draft (Authenticated/Follow-up)
        else if (!disableLocalDraft && result.draft && result.draft.answers) {
          draft = result.draft;
          console.log("🔄 Rehydrating from API draft");
        }
        // 2. Check Local Storage (Anonymous reloading)
        else if (!disableLocalDraft) {
          const localDraft = await loadDraftFromStorage(draftKey);
          if (localDraft && localDraft.templateId === templateId) {
            // Validate template version
            if (localDraft.templateVersion === questionnaireData.version) {
              draft = localDraft;
              console.log("🔄 Rehydrating from encrypted sessionStorage");
            } else {
              console.warn("⚠️ Draft version mismatch, discarding local draft");
              removeDraftFromStorage(draftKey);
            }
          }
        } else {
          removeDraftFromStorage(draftKey);
        }

        if (draft) {
          responsesRef.current = draft.answers || {};
          setResponses(draft.answers || {});
          if (!hasTreatmentAuthority && typeof draft.current_question_index === 'number') {
            setCurrentQuestionIndex(draft.current_question_index);
          } else if (!hasTreatmentAuthority && typeof draft.currentQuestionIndex === 'number') {
            setCurrentQuestionIndex(draft.currentQuestionIndex);
          }
          if (draft.patientInfo && typeof draft.patientInfo === 'object') {
            setPatientInfo(draft.patientInfo);
          } else if (draft.patient_id) {
            setPatientInfo((prev) => ({ ...prev, patient_id: draft.patient_id }));
          }
        }
        hasRehydrated.current = true;
      }
    } catch (err) {
      if (err?.code === "questionnaire_initialization_superseded") return;
      if ([
        "question_identity_ambiguous",
        "question_identity_duplicate",
      ].includes(err?.code)) {
        // An invalid immutable graph is not a recoverable partially rendered
        // questionnaire. Keep the prior/session answers in authority storage,
        // but remove the unusable projection so Retry cannot navigate it.
        setQuestionnaire(null);
        setQuestionIdMap({});
        setCurrentQuestionIndex(0);
        resumeCursorRef.current = null;
        pendingStableNavigationRef.current = null;
      }
      const message = getApiErrorMessage(err) || "Failed to initialize questionnaire";
      setError(message);
      if (
        runtimeProgramId
        || runtimeCustomProgramId
        || previewProgramId
        || previewCustomProgramId
        || previewCapabilityToken
      ) {
        setTreatmentRuntimeError({
          code: (
            err?.code
            || err?.response?.data?.error
            || "treatment_runtime_unavailable"
          ),
          message,
          status: err?.status || err?.response?.status || null,
          retryAfterSeconds: err?.retryAfterSeconds || null,
          action: err?.action || err?.response?.data?.action || null,
        });
      }
    } finally {
      if (initializationSequence === initializationSequenceRef.current) {
        setLoading(false);
        if (!resumeCursorRef.current) {
          setIsRehydrating(false);
        }
      }
    }
  }, [templateId, patientId, disableLocalDraft, draftKey, previewProgramId, previewApiBaseUrl, previewCustomProgramId, previewCapabilityToken, previewSnapshotChecksum, previewSnapshotId, isPreviewMode, runtimeProgramId, runtimeCustomProgramId, runtimeApiBaseUrl, runtimeAdapter]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const calculateBMI = useCallback(
    (currentResponses) => {
      if (!questionnaire?.questions) return null;

      const heightQuestion = questionnaire.questions.find(
        (q) =>
          q.question_type === "height_weight" ||
          q.question_type === "bmi" ||
          q.question_text.toLowerCase().includes("height")
      );

      if (!heightQuestion) return null;

      const heightResponse = currentResponses[heightQuestion.id];

      if (!heightResponse) return null;

      try {
        let totalInches = 0;
        let weightLbs = 0;

        if (
          typeof heightResponse === "object" &&
          heightResponse.feet &&
          heightResponse.inches !== undefined
        ) {
          totalInches =
            parseInt(heightResponse.feet) * 12 +
            parseInt(heightResponse.inches || 0);

          // Weight is now part of the height_weight response
          weightLbs = parseFloat(heightResponse.weight);
        } else {
          return null;
        }

        if (isNaN(weightLbs) || weightLbs <= 0) return null;

        // Don't calculate BMI for obviously incomplete weights (less than 50 lbs for adults)
        if (weightLbs < 50) return null;

        const bmi = (weightLbs / (totalInches * totalInches)) * 703;

        const bmiData = {
          bmi: Math.round(bmi * 10) / 10,
          heightInches: totalInches,
          weightLbs: weightLbs,
          weightKg: Math.round(weightLbs * 0.453592 * 100) / 100,
          age: patientInfo?.age || null,
        };

        return bmiData;
      } catch (error) {
        return null;
      }
    },
    [questionnaire, patientInfo]
  );

  const checkBMIEligibility = useCallback(
    (bmiData) => {
      if (!bmiData) {
        return { eligible: true };
      }

      const { bmi } = bmiData;

      // Prefer question-level bmi_min from BMI/height_weight question
      const bmiQuestion = questionnaire?.questions?.find(
        (q) => q.question_type === "height_weight" || q.question_type === "bmi"
      );
      const bmiMin = bmiQuestion?.validation?.bmi_min;

      if (bmiMin !== undefined && bmi < bmiMin) {
        return {
          eligible: false,
          reason: `Your BMI is ${bmi}. Our program is designed for individuals with a BMI of ${bmiMin} or higher.`,
          details: {
            currentBMI: bmi,
            requiredBMI: bmiMin,
            limitType: "min",
          },
        };
      }

      // Fallback: template-level eligibility_rules.bmi_rules (min only)
      const bmiRules = questionnaire?.eligibility_rules?.bmi_rules;
      if (bmiRules) {
        const treatmentNaiveMin = bmiRules.treatment_naive?.min || 25.0;
        if (bmi < treatmentNaiveMin) {
          return {
            eligible: false,
            reason: `BMI too low for treatment-naive patient (BMI: ${bmi}, required: ≥${treatmentNaiveMin})`,
            details: {
              currentBMI: bmi,
              requiredBMI: treatmentNaiveMin,
              patientType: "treatment_naive",
            },
          };
        }
      }

      return {
        eligible: true,
        message: `BMI ${bmi} meets eligibility requirements`,
      };
    },
    [questionnaire]
  );

  const evaluateConditionalLogic = useCallback(
    (question, currentResponses, visibleQuestionIds) => {
      const visibilityRules = firstRuleSet(
        question.visibility_rules,
        question.visibilityRules,
        question.conditional_logic
      );
      if (hasTreatmentAuthority && hasRuleContent(visibilityRules)) {
        // The authority projection can receive the authenticated preview
        // profile before React commits the matching questionnaire state. Read
        // the latest session context here so profile-gated questions do not
        // render once with the synthetic default profile.
        const runtimeVisibilityContext = (
          treatmentAuthorityRef.current?.visibility_context
        );
        const visibilityQuestionnaire = runtimeVisibilityContext
          ? {
              ...questionnaire,
              runtime_visibility_context: runtimeVisibilityContext,
            }
          : questionnaire;
        return isTreatmentQuestionVisible(
          question,
          visibilityQuestionnaire,
          currentResponses,
        );
      }

      const resolveQuestionId = (questionId) => {
        if (!questionId) return questionId;
        if (currentResponses.hasOwnProperty(questionId)) return questionId;
        const mappedQuestionId = questionIdMap[questionId];
        if (mappedQuestionId) return mappedQuestionId;
        const normalizedQuestionId = String(questionId);
        const matchedResponseKey = Object.keys(currentResponses || {}).find(
          (key) => key === normalizedQuestionId || key.endsWith(`-${normalizedQuestionId}`)
        );
        return matchedResponseKey || questionId;
      };

      const getQuestionById = (questionId) =>
        questionnaire?.questions?.find((item) => item.id === questionId);

      const getFieldValue = (sourceValue, fieldPath) => {
        if (!fieldPath || sourceValue === undefined || sourceValue === null) {
          return sourceValue;
        }
        return String(fieldPath)
          .split(".")
          .filter(Boolean)
          .reduce((acc, key) => (acc !== undefined && acc !== null ? acc[key] : undefined), sourceValue);
      };

      const compareCondition = (responseValue, operator, expectedValue) => {
        switch (operator) {
          case "equals":
          case "eq":
            return Array.isArray(responseValue)
              ? responseValue.map(String).includes(String(expectedValue))
              : String(responseValue) === String(expectedValue);
          case "not_equals":
          case "neq":
            return Array.isArray(responseValue)
              ? !responseValue.map(String).includes(String(expectedValue))
              : String(responseValue) !== String(expectedValue);
          case "contains":
            return Array.isArray(responseValue)
              ? responseValue.includes(expectedValue)
              : String(responseValue || "").includes(String(expectedValue || ""));
          case "not_contains":
            return Array.isArray(responseValue)
              ? !responseValue.includes(expectedValue)
              : !String(responseValue || "").includes(String(expectedValue || ""));
          case "in":
            if (!Array.isArray(expectedValue)) return false;
            return Array.isArray(responseValue)
              ? responseValue.some((val) => expectedValue.includes(val))
              : expectedValue.includes(responseValue);
          case "not_in":
            if (!Array.isArray(expectedValue)) return true;
            return Array.isArray(responseValue)
              ? !responseValue.some((val) => expectedValue.includes(val))
              : !expectedValue.includes(responseValue);
          case "gt":
          case "greater_than":
            return Number(responseValue) > Number(expectedValue);
          case "gte":
          case "greater_than_or_equal":
            return Number(responseValue) >= Number(expectedValue);
          case "lt":
          case "less_than":
            return Number(responseValue) < Number(expectedValue);
          case "lte":
          case "less_than_or_equal":
            return Number(responseValue) <= Number(expectedValue);
          case "between": {
            let min, max;
            if (Array.isArray(expectedValue) && expectedValue.length === 2) {
              min = Number(expectedValue[0]);
              max = Number(expectedValue[1]);
            } else if (typeof expectedValue === "string") {
              const parts = expectedValue.split(",").map((s) => s.trim());
              if (parts.length !== 2) return false;
              min = Number(parts[0]);
              max = Number(parts[1]);
            } else {
              return false;
            }
            const n = Number(responseValue);
            if (isNaN(n) || isNaN(min) || isNaN(max)) return false;
            return n >= Math.min(min, max) && n <= Math.max(min, max);
          }
          case "is_empty":
            return (
              responseValue === undefined ||
              responseValue === null ||
              responseValue === "" ||
              (Array.isArray(responseValue) && responseValue.length === 0)
            );
          case "is_not_empty":
            return !(
              responseValue === undefined ||
              responseValue === null ||
              responseValue === "" ||
              (Array.isArray(responseValue) && responseValue.length === 0)
            );
          default:
            return true;
        }
      };

      const evaluateConditionNode = (node) => {
        if (!node) return true;
        if (node.mode && (Array.isArray(node.rules) || Array.isArray(node.subgroups))) {
          const children = [
            ...(Array.isArray(node.rules)
              ? node.rules.map((rule) => ({
                  type: "condition",
                  question_id: rule.question_id || rule.questionId,
                  question_type: rule.question_type,
                  operator: rule.operator,
                  value: rule.value,
                  field: rule.field,
                }))
              : []),
            ...(Array.isArray(node.subgroups) ? node.subgroups : []),
          ];
          if (!children.length) return true;
          const operator = String(
            node.operator || (node.mode === "simple" ? "OR" : "AND")
          ).toUpperCase();
          return operator === "OR"
            ? children.some((child) => evaluateConditionNode(child))
            : children.every((child) => evaluateConditionNode(child));
        }
        if (node.type === "group") {
          const children = Array.isArray(node.children) ? node.children : [];
          if (!children.length) return true;
          const evaluator =
            String(node.operator || "AND").toUpperCase() === "OR" ? "some" : "every";
          return children[evaluator]((child) => evaluateConditionNode(child));
        }

        const source = node.source || "answer";
        let responseValue;

        if (node.question_type === "bmi") {
          const bmiData = calculateBMI(currentResponses);
          responseValue = bmiData?.bmi;
        } else if (source === "branch") {
          responseValue = getFieldValue(currentResponses.__branchState || {}, node.key || node.field);
        } else if (source === "derived") {
          responseValue = getFieldValue(currentResponses.__derivedState || {}, node.key || node.field);
        } else {
          const responseKey = resolveQuestionId(node.question_id);
          // If the question we depend on is explicitly marked as NOT visible,
          // its response should be treated as empty. This prevents stale
          // answers from keeping nested questions visible.
          if (visibleQuestionIds && responseKey && !visibleQuestionIds.has(responseKey)) {
            responseValue = undefined;
          } else {
            responseValue = getFieldValue(currentResponses[responseKey], node.field);
          }
        }

        if (
          (responseValue === undefined || responseValue === null) &&
          !["is_empty", "is_not_empty"].includes(node.operator)
        ) {
          return false;
        }

        return compareCondition(responseValue, node.operator, node.value);
      };

      if (visibilityRules?.show_if || visibilityRules?.skip_if) {
        const shouldSkip = visibilityRules.skip_if
          ? evaluateConditionNode(visibilityRules.skip_if)
          : false;
        if (shouldSkip) {
          return false;
        }

        if (visibilityRules.show_if) {
          return evaluateConditionNode(visibilityRules.show_if);
        }
        return true;
      }

      if (visibilityRules?.mode) {
        return evaluateConditionNode(visibilityRules);
      }

      if (
        visibilityRules &&
        (visibilityRules.field ||
          visibilityRules.question_id ||
          visibilityRules.questionId) &&
        visibilityRules.operator
      ) {
        return evaluateConditionNode({
          type: "condition",
          question_id:
            visibilityRules.question_id ||
            visibilityRules.questionId ||
            visibilityRules.field,
          operator: visibilityRules.operator,
          value: visibilityRules.value,
        });
      }

      if (!question.conditions?.length) return true;
      if (!Object.keys(currentResponses).length) {
        return !question.conditions.some((c) => c.type === "showIf");
      }

      const logicOperator = question.logic_operator || "AND";
      const evaluateMethod =
        logicOperator === "OR"
          ? question.conditions.some.bind(question.conditions)
          : question.conditions.every.bind(question.conditions);

      return evaluateMethod((condition) =>
        evaluateConditionNode({
          type: "condition",
          source: condition.source || (condition.field ? "answer_field" : "answer"),
          question_id: condition.question_id,
          field: condition.field,
          key: condition.key,
          operator: condition.operator,
          value: condition.value,
        })
      );
    },
    [
      questionIdMap,
      calculateBMI,
      hasTreatmentAuthority,
      questionnaire,
    ]
  );

  /**
   * Core visibility computation extracted into a plain function so it can be
   * invoked with any responses object — including fresh ones that haven't
   * been committed to React state yet (e.g. inside a setTimeout).
   */
  const computeVisibleQuestions = useCallback((responsesArg) => {
    if (!questionnaire?.questions) return [];

    const visibleQuestionIds = new Set();
    let emittedServiceAreaQuestion = false;

    const hasAuthoritativeProductProjection = (
      hasTreatmentAuthority
      && treatmentAuthorityRef.current?.state
        === TREATMENT_RUNTIME_STATE.productSelection
    );
    const hasAuthoritativeLabProjection = (
      hasTreatmentAuthority
      && treatmentAuthorityRef.current?.state
        === TREATMENT_RUNTIME_STATE.labSelection
    );
    const authoritativeRuntimeState = treatmentAuthorityRef.current?.state;
    const projectedQuestions = (
      hasAuthoritativeProductProjection || hasAuthoritativeLabProjection
    )
      ? projectRuntimeQuestionnaireQuestions(
          questionnaire.questions,
          treatmentAuthorityRef.current,
        )
      : questionnaire.questions;

    const questionsForRendering = hasTreatmentAuthority
      ? orderQuestionsByRuntimeBoundary(projectedQuestions)
      : projectedQuestions;

    return questionsForRendering.filter((question, index) => {
      if (question.runtime_only) return false;
      if (
        hasTreatmentAuthority
        && questionIsBeforeRuntimeBoundary(
          question,
          authoritativeRuntimeState,
        )
      ) {
        visibleQuestionIds.add(question.id);
        return false;
      }
      if (
        hasTreatmentAuthority
        && !questionIsInCurrentRuntimeStep(
          question,
          treatmentAuthorityRef.current,
        )
      ) {
        visibleQuestionIds.add(question.id);
        return false;
      }
      // Hide questions explicitly marked as hidden (system/derived values)
      if (question.validation?.hidden === true || question.validation_rules?.hidden === true) {
        visibleQuestionIds.add(question.id);
        return false;
      }

      // Product eligibility comes from the current runtime projection. This
      // removes hidden medication branches and stale options before the
      // questionnaire renders or submits them.
      if (hasAuthoritativeProductProjection && question.question_type === "product_selection") {
        visibleQuestionIds.add(question.id);
        return true;
      }

      // Hide a derived-prefilled question once a derived value is already available.
      const hideWhenDerivedPrefilled =
        question.validation?.hide_when_derived_prefilled === true ||
        question.validation_rules?.hide_when_derived_prefilled === true;
      if (hideWhenDerivedPrefilled) {
        const currentValue = responsesArg[question.id];
        const hasValue =
          currentValue !== undefined &&
          currentValue !== null &&
          String(currentValue).trim() !== "";
        const hasSourceMetadata = Object.keys(prefillSources || {}).length > 0;
        const source = prefillSources?.[question.id];
        const wasDerivedPrefill =
          source === "derived" || source === "product_context";
        if (hasValue && (!hasSourceMetadata || wasDerivedPrefill)) {
          visibleQuestionIds.add(question.id);
          return false;
        }
      }

      // Skip the separate weight question if we have a height_weight or bmi question
      if (
        question.question_type === "number" &&
        question.question_text.toLowerCase().includes("weight") &&
        questionsForRendering.some((q) => q.question_type === "height_weight" || q.question_type === "bmi")
      ) {
        return false;
      }

      // Skip the BMI question in the UI - it's auto-populated by HeightWeight component
      if (
        question.question_type === "number" &&
        (question.question_text.toLowerCase().includes("bmi") ||
          question.question_text.toLowerCase().includes("your bmi is"))
      ) {
        return false;
      }

      const firstQuestionHasVisibilityRules = hasRuleContent(firstRuleSet(
        question.visibility_rules,
        question.visibilityRules,
        question.conditional_logic,
      ));
      if (
        index === 0
        && !Object.keys(responsesArg).length
        && !firstQuestionHasVisibilityRules
      ) {
        visibleQuestionIds.add(question.id);
        return true;
      }

      const passesConditionalLogic = evaluateConditionalLogic(question, responsesArg, visibleQuestionIds);
      if (!passesConditionalLogic) {
        return false;
      }

      // The Lab runtime question owns the page chrome. If every lab is hidden
      // by its own visibility rule, rendering it produces an empty page with
      // only the generic header and Continue button. Treat that as no Lab
      // step at all, using the same selector as the component and navigation.
      if (
        question.question_type === "program_lab_checkout"
        && !hasVisibleProgramLabs(question, questionnaire, responsesArg)
      ) {
        return false;
      }

      // A Custom Program may inherit the same Service Area Check from more
      // than one included Program. Care location is one runtime boundary;
      // rendering another copy later would make Continue synchronize back to
      // routing and can repeat the earlier intake questions.
      if (hasTreatmentAuthority && question.question_type === "state_routing") {
        if (emittedServiceAreaQuestion) return false;
        emittedServiceAreaQuestion = true;
      }

      // Patient info questions should still be visible by default, but they
      // must not bypass explicit conditional display rules.
      if (inferPatientInfoMapping(question)) {
        visibleQuestionIds.add(question.id);
        return true;
      }

      visibleQuestionIds.add(question.id);
      return true;
    });
  }, [questionnaire, evaluateConditionalLogic, prefillSources, hasTreatmentAuthority]);

  // Memoised wrapper used for rendering: always uses committed state.
  const getVisibleQuestions = useCallback((responsesOverride) => {
    return computeVisibleQuestions(responsesOverride !== undefined ? responsesOverride : responsesRef.current);
  }, [computeVisibleQuestions]);

  const getCurrentQuestion = useCallback(() => {
    const visibleQuestions = getVisibleQuestions();
    if (!visibleQuestions.length) return null;

    let index = currentQuestionIndex;
    if (index >= visibleQuestions.length) index = visibleQuestions.length - 1;
    if (index < 0) index = 0;

    return visibleQuestions[index];
  }, [currentQuestionIndex, getVisibleQuestions]);

  useEffect(() => {
    const cursor = resumeCursorRef.current;
    if (!cursor || !questionnaire) return;
    const visible = getVisibleQuestions(responsesRef.current);
    const targetIndex = cursor.projection
      ? resolveAuthoritativeNodeIndex({
          allQuestions: hasTreatmentAuthority
            ? orderQuestionsByRuntimeBoundary(questionnaire.questions)
            : questionnaire.questions,
          visibleQuestions: visible,
          browserNodeId: cursor.itemId,
          projection: cursor.projection,
          stateForQuestion: authorityStateForQuestion,
        })
      : resolveStableNodeIndex({
          allQuestions: hasTreatmentAuthority
            ? orderQuestionsByRuntimeBoundary(questionnaire.questions)
            : questionnaire.questions,
          visibleQuestions: visible,
          currentNodeId: cursor.itemId,
          fallbackState: cursor.state,
          stateForQuestion: authorityStateForQuestion,
        });
    setCurrentQuestionIndex(targetIndex >= 0 ? targetIndex : 0);
    setIsRehydrating(false);
    resumeCursorRef.current = null;
  }, [getVisibleQuestions, questionnaire, hasTreatmentAuthority]);

  // Runtime boundary synchronization can replace the visibility context and
  // therefore change numeric indexes. Keep Back navigation anchored to the
  // released node identity until the refreshed projection has rendered.
  const navigateToStableQuestion = useCallback((itemId, fallbackState = null) => {
    if (!itemId) return false;
    pendingStableNavigationRef.current = {
      itemId: String(itemId),
      state: fallbackState,
    };
    // The target is intentionally held by identity until the authoritative
    // projection has settled. The ref itself does not trigger a render, so
    // bump a local revision to make this work even when the server response
    // leaves the questionnaire object unchanged.
    setStableNavigationRevision((revision) => revision + 1);
    return true;
  }, []);

  useEffect(() => {
    const cursor = pendingStableNavigationRef.current;
    if (!cursor || !questionnaire || isRehydrating) return;
    const visible = getVisibleQuestions(responsesRef.current);
    const targetIndex = resolveStableNodeIndex({
      allQuestions: hasTreatmentAuthority
        ? orderQuestionsByRuntimeBoundary(questionnaire.questions)
        : questionnaire.questions,
      visibleQuestions: visible,
      currentNodeId: cursor.itemId,
      fallbackState: cursor.state,
      stateForQuestion: authorityStateForQuestion,
    });
    if (targetIndex < 0 || !visible[targetIndex]) {
      // A released node can disappear after a server-authoritative
      // reconciliation (for example, a consent was pruned or a Program was
      // deselected).  Do not leave the cursor pending forever: that made the
      // editor appear frozen and could trigger repeated boundary requests.
      pendingStableNavigationRef.current = null;
      setIsRehydrating(false);
      setTreatmentRuntimeError({
        code: "runtime_navigation_target_unavailable",
        message: "That step is no longer available. Refresh and try again.",
        status: null,
        action: null,
      });
      return;
    }
    setCurrentQuestionIndex(targetIndex);
    setQuestionStartTime(Date.now());
    flushDraftToStorageRef.current?.(responsesRef.current, targetIndex);
    pendingStableNavigationRef.current = null;
  }, [
    getVisibleQuestions,
    isRehydrating,
    questionnaire,
    stableNavigationRevision,
    hasTreatmentAuthority,
  ]);

  useEffect(() => {
    if (
      !treatmentAuthorityRef.current
      || (!runtimeSourceId && !previewCapabilityToken)
    ) return;
    const question = getCurrentQuestion();
    const currentItemId = runtimePointerNodeId({
      currentQuestion: question,
      projection: treatmentAuthorityRef.current,
      stateForQuestion: authorityStateForQuestion,
    });
    if (previewCapabilityToken) {
      persistTreatmentPreviewSession(
        previewCapabilityToken,
        treatmentAuthorityRef.current,
        { currentItemId },
      );
    } else {
      persistTreatmentAuthority(treatmentAuthorityRef.current, {
        sourceKind: runtimeSourceKind,
        sourceId: runtimeSourceId,
        currentItemId,
      });
    }
  }, [
    currentQuestionIndex,
    getCurrentQuestion,
    previewCapabilityToken,
    runtimeSourceId,
    runtimeSourceKind,
  ]);

  const validateResponse = useCallback(
    (question, responseValue, showImmediate = false) => {
      const errors = [];
      const required = isQuestionRequired(question);

      // Handle required field validation
      // Skip consent questions from generic required check - they have custom validation below
      if (required && question.question_type !== "consent") {
        if (isEmptyQuestionResponse(responseValue)) {
          errors.push("This field is required");
        }
      }

      switch (question.question_type) {
        case "product_selection":
          if (required && isEmptyQuestionResponse(responseValue)) {
            errors.push(
              "Select a treatment option before continuing.",
            );
          }
          break;

        case "consent":
          // Consent validation: check if a consent option was selected
          // This is the ONLY place consent is validated to avoid duplicate errors
          if (required) {
            const hasValidConsent = responseValue &&
              (responseValue.consent_text || responseValue === true || responseValue.agreed !== undefined);
            if (!hasValidConsent) {
              errors.push("You must select a consent option to continue");
            }
          }
          break;

        case "single_choice":
        case "labs":
          const choiceValues = (question.answer_choices || []).map((choice) => {
            if (typeof choice === "string") return choice;
            if (choice && typeof choice === "object") {
              return choice.value || choice.label || choice.product_id || choice.id;
            }
            return choice;
          });
          if (
            responseValue &&
            !choiceValues.includes(responseValue)
          ) {
            errors.push("Please select a valid option");
          }
          break;

        case "multiple_choice":
          if (Array.isArray(responseValue)) {
            const invalid = responseValue.filter(
              (c) => !question.answer_choices?.includes(c)
            );
            if (invalid.length) errors.push("Please select only valid options");
          }
          break;

        case "number":
          if (
            responseValue !== undefined &&
            responseValue !== "" &&
            responseValue !== null
          ) {
            const num = parseFloat(responseValue);
            if (isNaN(num)) {
              errors.push("Please enter a valid number");
            } else {
              if (question.validation?.min && num < question.validation.min) {
                errors.push(
                  `Value must be at least ${question.validation.min}${question.validation?.unit || ""
                  }`
                );
              }
              if (question.validation?.max && num > question.validation.max) {
                errors.push(
                  `Value must be at most ${question.validation.max}${question.validation?.unit || ""
                  }`
                );
              }
            }
          }
          break;

        case "height_weight":
        case "bmi":
          if (responseValue) {
            if (
              !responseValue.feet ||
              responseValue.feet < 3 ||
              responseValue.feet > 8
            ) {
              errors.push("Height feet must be between 3 and 8");
            }
            if (responseValue.inches < 0 || responseValue.inches > 11) {
              errors.push("Height inches must be between 0 and 11");
            }
          }
          break;

        case "file_upload":
          if (
            responseValue &&
            typeof responseValue === "object" &&
            responseValue.filename
          ) {
            const validation = validateUploadFile(
              {
                name: responseValue.filename,
                size: responseValue.fileSize,
              },
              question.upload_config,
            );
            if (!validation.valid) errors.push(validation.error);
          }
          break;
        case "shipping_address":
        case "personal_details":
          {
            const requiredFieldError = requiredGroupedFieldError(
              question,
              responseValue,
            );
            if (requiredFieldError) errors.push(requiredFieldError);
          }
          // Mandatory consent for personal_details only
          if (question.question_type === "personal_details") {
            const consentAccepted =
              responseValue &&
              typeof responseValue === "object" &&
              responseValue.__consent_accepted === true;
            if (!consentAccepted) {
              errors.push(
                "You must agree to the Terms, Privacy Policy, and Telehealth consent to continue."
              );
            }
          }

          // Validate sub-fields within grouped questions
          if (responseValue && typeof responseValue === "object" && question.sub_questions) {
            question.sub_questions.forEach((subQ) => {
              const subValue = responseValue[subQ.id];
              const label = subQ.question_text?.toLowerCase() || "";


              // Email validation
              if (label.includes("email") && subValue) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subValue)) {
                  errors.push("Please enter a valid email address");
                }
              }

              // // Phone number validation
              if (label.toLowerCase().includes("phone") && subValue) {
                if (getUsPhoneState(subValue).status !== "valid") {
                  errors.push("Please enter a valid US phone number");
                }

                // send normalized to backend
              }



              // State validation: exactly 2 letters
              if (label.includes("state") && subValue) {
                if (!/^[a-zA-Z]{2}$/.test(subValue)) {
                  errors.push("State must be exactly 2 letters");
                }
              }

              // Zip validation: max 10 alphanumeric
              if (label.includes("zip") && subValue) {
                if (!/^[a-zA-Z0-9]{1,10}$/.test(subValue)) {
                  errors.push("Zip code must be max 10 alphanumeric characters");
                }
              }



            });
          }
          break;
      }

      if (showImmediate) {
        setValidationErrors((prev) => ({
          ...prev,
          [question.id]: errors,
        }));
      }

      return errors;
    },
    []
  );

  // UPDATED: Debounced BMI check function
  const debouncedBMICheck = useCallback(
    (allResponses) => {
      const bmiData = calculateBMI(allResponses);
      if (bmiData) {
        setCalculatedBMI(bmiData);
        if (hasTreatmentAuthority) return;
        const bmiEligibility = checkBMIEligibility(bmiData);

        if (!bmiEligibility.eligible) {
          setDisqualified(true);
          setDisqualificationReason({
            disqualified: true,
            reason: bmiEligibility.reason,
            questionText: "BMI Eligibility Check",
            bmiData: bmiData,
          });
        }
      }
    },
    [calculateBMI, checkBMIEligibility, hasTreatmentAuthority]
  );

  // UPDATED: More intelligent disqualification check
  const checkEarlyDisqualification = useCallback(
    (questionId, responseValue, allResponses) => {
      // Every treatment runtime session is server-authoritative. In a Custom
      // Program, a local global disqualification would incorrectly terminate
      // eligible siblings before the server partitions the affected branch.
      if (!shouldEvaluateDisqualificationLocally({
        hasTreatmentAuthority,
        runtimeSourceKind,
      })) {
        return { disqualified: false };
      }
      const question = questionnaire?.questions.find(
        (q) => q.id === questionId
      );
      const questionResult = evaluateQuestionDisqualification({
        question,
        questionnaire,
        responses: allResponses,
        responseValue,
        allowTopLevelDqChoices:
          runtimeSourceKind === TREATMENT_SOURCE_KIND.customProgram,
      });
      if (questionResult.disqualified) {
        return questionResult;
      }

      // Handle global eligibility rules immediately
      if (questionnaire?.eligibility_rules?.disqualifying_conditions) {
        // Skip consent responses - they should not be checked against disqualifying conditions
        if (question?.question_type === "consent") {
          return { disqualified: false };
        }

        // For multiple choice questions, check if response is an array
        let responseText;
        if (Array.isArray(responseValue)) {
          // For arrays, check each item individually
          responseText = responseValue.join("|"); // Use separator to avoid false matches
        } else if (typeof responseValue === "string") {
          responseText = responseValue;
        } else if (typeof responseValue === "object") {
          // For objects (like consent responses), skip the check
          return { disqualified: false };
        } else {
          responseText = String(responseValue);
        }

        const matchingCondition =
          questionnaire.eligibility_rules.disqualifying_conditions.find(
            (condition) =>
              responseText &&
              responseText.toLowerCase().includes(condition.toLowerCase())
          );

        if (matchingCondition) {
          return {
            disqualified: true,
            reason: `Your response indicates "${matchingCondition}", which makes you ineligible for this treatment at this time.`,
            questionText: question?.question_text,
            disqualifyingCondition: matchingCondition,
            questionId: questionId, // Track which question caused disqualification
          };
        }
      }

      // Handle weight input with debouncing - only check BMI after user stops typing
      if (
        question?.question_type === "number" &&
        question.question_text.toLowerCase().includes("weight")
      ) {
        // Clear existing timer
        if (weightDebounceTimer.current) {
          clearTimeout(weightDebounceTimer.current);
        }

        // Only proceed with BMI check if weight seems reasonable (>= 50 lbs)
        const weight = parseFloat(responseValue);
        if (!isNaN(weight) && weight >= 50) {
          // Set a timer to check BMI after user stops typing for 1.5 seconds
          weightDebounceTimer.current = setTimeout(() => {
            debouncedBMICheck(allResponses);
          }, 1500);
        }
      }

      return { disqualified: false };
    },
    [
      questionnaire,
      debouncedBMICheck,
      hasTreatmentAuthority,
      runtimeSourceKind,
    ]
  );

  const updateResponse = useCallback(
    (questionId, responseValue) => {
      lastResponseDisqualifiedRef.current = false;
      const timeSpent = Date.now() - (questionStartTime || Date.now());
      const previousResponses = responsesRef.current;
      const newResponses = { ...previousResponses, [questionId]: responseValue };

      // 🆕 Auto-populate BMI question when height/weight is entered
      const currentQuestion = questionnaire?.questions.find(
        (q) => q.id === questionId
      );
      if ((currentQuestion?.question_type === "height_weight" || currentQuestion?.question_type === "bmi") && responseValue) {
        // Calculate BMI from the height/weight response
        const bmiData = calculateBMI(newResponses);

        if (bmiData && bmiData.bmi) {
          // Update the calculatedBMI state
          setCalculatedBMI(bmiData);

          // Find the BMI question
          const bmiQuestion = questionnaire.questions.find(
            (q) =>
              q.question_type === "number" &&
              (q.question_text.toLowerCase().includes("bmi") ||
                q.question_text.toLowerCase().includes("your bmi is"))
          );

          if (bmiQuestion) {
            // Auto-populate the BMI question with calculated value
            newResponses[bmiQuestion.id] = bmiData.bmi;
            console.log(
              `🔢 Auto-populated BMI question with value: ${bmiData.bmi}`
            );
            console.log(`🔢 Updated calculatedBMI state:`, bmiData);
          }
        }
      }

      const reconciledResponses = hasTreatmentAuthority
        ? pruneNewlyHiddenProgramResponses(
            questionnaire,
            previousResponses,
            newResponses,
          )
        : newResponses;

      if (!disqualified) {
        const disqualificationResult = checkEarlyDisqualification(
          questionId,
          responseValue,
          reconciledResponses
        );

        if (disqualificationResult.disqualified) {
          lastResponseDisqualifiedRef.current = true;
          setDisqualified(true);
          setDisqualificationReason(disqualificationResult);
          responsesRef.current = reconciledResponses;
          setResponses(reconciledResponses);
          return reconciledResponses;
        }
      }

      responsesRef.current = reconciledResponses;
      setResponses(reconciledResponses);
      if (previewCapabilityToken && treatmentAuthorityRef.current) {
        persistTreatmentPreviewSession(
          previewCapabilityToken,
          treatmentAuthorityRef.current,
          { responses: reconciledResponses },
        );
      } else if (runtimeSourceId && treatmentAuthorityRef.current) {
        persistTreatmentAuthority(treatmentAuthorityRef.current, {
          sourceKind: runtimeSourceKind,
          sourceId: runtimeSourceId,
          responses: reconciledResponses,
        });
      }

      // Don't validate immediately - only validate when user clicks Continue
      // This prevents premature error messages while user is still typing

      setNavigationHistory((prev) => [
        ...prev,
        {
          question_id: questionId,
          response_value: responseValue,
          time_spent_ms: timeSpent,
          timestamp: new Date().toISOString(),
        },
      ]);

      setQuestionStartTime(Date.now());

      return reconciledResponses;
    },
    [
      questionStartTime,
      disqualified,
      checkEarlyDisqualification,
      hasTreatmentAuthority,
      questionnaire,
      validateResponse,
      responses,
      calculateBMI,
      previewCapabilityToken,
      runtimeSourceId,
      runtimeSourceKind,
    ]
  );

  const wasLastResponseDisqualified = useCallback(
    () => lastResponseDisqualifiedRef.current,
    [],
  );

  // Batch update multiple responses at once (for prefill scenarios)
  // This avoids stale state issues when calling updateResponse in a loop
  const batchUpdateResponses = useCallback(
    (responsesToMerge) => {
      if (!responsesToMerge || Object.keys(responsesToMerge).length === 0) {
        return;
      }
      console.log('📦 [useFrontendQuestionnaire] Batch updating responses:', responsesToMerge);
      const newResponses = {
        ...responsesRef.current,
        ...responsesToMerge,
      };
      responsesRef.current = newResponses;
      setResponses(newResponses);
      if (previewCapabilityToken && treatmentAuthorityRef.current) {
        persistTreatmentPreviewSession(
          previewCapabilityToken,
          treatmentAuthorityRef.current,
          { responses: newResponses },
        );
      } else if (runtimeSourceId && treatmentAuthorityRef.current) {
        persistTreatmentAuthority(treatmentAuthorityRef.current, {
          sourceKind: runtimeSourceKind,
          sourceId: runtimeSourceId,
          responses: newResponses,
        });
      }
      return newResponses;
    },
    [
      previewCapabilityToken,
      runtimeSourceId,
      runtimeSourceKind,
    ]
  );

  const getResponsesSnapshot = useCallback(
    () => ({ ...responsesRef.current }),
    [],
  );

  const clearQuestionResponses = useCallback((questionIds) => {
    const ids = new Set((questionIds || []).map(String));
    if (!ids.size) return responsesRef.current;
    const nextResponses = { ...responsesRef.current };
    Object.keys(nextResponses).forEach((questionId) => {
      if (ids.has(String(questionId))) delete nextResponses[questionId];
    });
    responsesRef.current = nextResponses;
    setResponses(nextResponses);
    if (runtimeSourceId && treatmentAuthorityRef.current) {
      persistTreatmentAuthority(treatmentAuthorityRef.current, {
        sourceKind: runtimeSourceKind,
        sourceId: runtimeSourceId,
        responses: nextResponses,
      });
    }
    return nextResponses;
  }, [runtimeSourceId, runtimeSourceKind]);

  const resetTreatmentPatientState = useCallback(async () => {
    if (!runtimeSourceId) return null;
    removeDraftFromStorage(activeDraftKeyRef.current);
    removeDraftFromStorage(draftKey);
    clearTreatmentAuthority();
    setTreatmentAuthorityRef(null);
    treatmentCheckoutRef.current = null;
    treatmentCheckoutAttemptRef.current = null;
    resumeCursorRef.current = null;
    pendingStableNavigationRef.current = null;
    responsesRef.current = {};
    setResponses({});
    setValidationErrors({});
    setPatientInfo(null);
    setDisqualified(false);
    setDisqualificationReason(null);
    setCalculatedBMI(null);
    setTreatmentCheckout(null);
    setTreatmentCheckoutRecovery(null);
    setTreatmentQuoteChange(null);
    setTreatmentRecoveryMessage(null);
    setCompleted(false);
    setCurrentQuestionIndex(0);
    await initialize();
    return treatmentAuthorityRef.current;
  }, [draftKey, initialize, runtimeSourceId]);

  const installAuthoritativeTreatmentProjection = useCallback((
    projection,
    {
      preservePendingResponses = false,
      broadcast = true,
      preservePreviewAuthentication = false,
    } = {},
  ) => {
    if (!projection) return null;
    const normalizedProjection = normalizeRuntimeAuthority(projection);
    const prior = treatmentAuthorityRef.current;
    if (
      prior
      && normalizedProjection.session_token === prior.session_token
      && treatmentRuntimeRevision(normalizedProjection)
        < treatmentRuntimeRevision(prior)
    ) {
      return prior;
    }
    const installed = {
      ...normalizedProjection,
      ...(prior?.preview_capability_token
        ? { preview_capability_token: prior.preview_capability_token }
        : {}),
      ...(prior?.preview_api_base_url
        ? { preview_api_base_url: prior.preview_api_base_url }
        : {}),
      ...(prior?.runtime_api_base_url
        ? { runtime_api_base_url: prior.runtime_api_base_url }
        : {}),
    };
    setTreatmentAuthorityRef(installed);
    if (installed.visibility_context) {
      setQuestionnaire((currentQuestionnaire) => (
        currentQuestionnaire
          ? {
              ...currentQuestionnaire,
              runtime_visibility_context: installed.visibility_context,
            }
          : currentQuestionnaire
      ));
    }
    const restored = reconcileAuthorityResponses({
      questionnaire,
      session: installed,
      pendingResponses: responsesRef.current,
      preservePendingResponses,
    });
    responsesRef.current = restored;
    setResponses(restored);
    const disqualification = runtimeDisqualificationFromProjection({
      projection: installed,
      questionnaire,
      fallbackQuestion: getCurrentQuestion(),
    });
    setDisqualified(Boolean(disqualification));
    setDisqualificationReason(disqualification);
    const currentQuestion = getCurrentQuestion();
    const currentItemId = runtimePointerNodeId({
      currentQuestion,
      projection: installed,
      stateForQuestion: authorityStateForQuestion,
    });
    const preserveCurrentPreviewAuthentication = shouldPreservePreviewAuthenticationCursor({
      enabled: preservePreviewAuthentication,
      previewCapabilityToken,
      currentQuestion,
      projection: installed,
    });
    if (
      !preserveCurrentPreviewAuthentication
      && authorityCursorRequiresNavigation({
        currentQuestion,
        projection: installed,
        stateForQuestion: authorityStateForQuestion,
      })
    ) {
      navigateToStableQuestion(currentItemId, installed.state);
    }
    try {
      if (previewCapabilityToken) {
        persistTreatmentPreviewSession(previewCapabilityToken, installed, {
          currentItemId,
        });
      } else {
        persistTreatmentAuthority(installed, {
          sourceKind: runtimeSourceKind,
          sourceId: runtimeSourceId,
          currentItemId,
        });
      }
    } catch (persistenceError) {
      // A malformed/legacy projection must not crash the questionnaire render
      // or trigger a silent navigation loop. Keep the server projection in
      // memory and expose a safe recovery message; support details remain in
      // development diagnostics only.
      const message = "This treatment session could not be saved. Refresh or start over.";
      setError(message);
      setTreatmentRuntimeError({
        code: persistenceError?.code || "runtime_pointer_invalid",
        message,
        status: persistenceError?.status || null,
        action: { code: "start_over", label: "Start over" },
      });
      if (process.env.NODE_ENV === "development") {
        console.debug("[TreatmentRuntime] authority pointer persistence failed", persistenceError);
      }
    }
    if (
      broadcast
      && treatmentRuntimeChannelRef.current
      && treatmentRuntimeRevision(installed) > treatmentRuntimeRevision(prior)
    ) {
      treatmentRuntimeChannelRef.current.postMessage({
        sourceKind: runtimeSourceKind || null,
        sourceId: runtimeSourceId || null,
        releaseId: installed.release?.id || null,
        releaseChecksum: installed.release?.checksum || null,
        sessionToken: installed.session_token,
        revision: installed.session_revision,
      });
    }
    return installed;
  }, [
    getCurrentQuestion,
    navigateToStableQuestion,
    previewCapabilityToken,
    questionnaire,
    runtimeSourceId,
    runtimeSourceKind,
    setError,
    setTreatmentRuntimeError,
  ]);

  useEffect(() => {
    if (!runtimeSourceId && !previewCapabilityToken) return undefined;
    const clearReconcileTimer = () => {
      if (treatmentRuntimeReconcileTimerRef.current !== null) {
        window.clearTimeout(treatmentRuntimeReconcileTimerRef.current);
        treatmentRuntimeReconcileTimerRef.current = null;
      }
    };
    const reconcileNow = ({ pageRestore = false } = {}) => {
      if (
        document.visibilityState === "hidden"
        || treatmentUploadInFlightRef.current > 0
        || !treatmentAuthorityRef.current
      ) {
        return null;
      }
      if (treatmentRuntimeReconcileInFlightRef.current) {
        return treatmentRuntimeReconcileInFlightRef.current;
      }
      const session = treatmentAuthorityRef.current;
      if (pageRestore) setTreatmentRuntimeError(null);
      const request = reconcileTreatmentSession({
        session,
        getSession: getTreatmentSession,
        installProjection: pageRestore
          ? installAuthoritativeTreatmentProjection
          : (projection) => (
            isNewerTreatmentRuntimeProjection(
              projection,
              treatmentAuthorityRef.current,
            )
              ? installAuthoritativeTreatmentProjection(projection, {
                  preservePendingResponses: true,
                })
              : treatmentAuthorityRef.current
          ),
      })
        .catch((reconciliationError) => {
          if (pageRestore) {
            reconciliationError.code = (
              reconciliationError.code || "runtime_resume_failed"
            );
            const message = getApiErrorMessage(reconciliationError)
              || "Saved treatment progress could not be reconciled.";
            setError(message);
            setTreatmentRuntimeError({
              code: reconciliationError.code,
              message,
              status: reconciliationError.status || null,
              retryAfterSeconds: reconciliationError.retryAfterSeconds || null,
              action: reconciliationError.action || null,
            });
          } else if (
            reconciliationError?.status !== 409
            && process.env.NODE_ENV === "development"
          ) {
            console.debug(
              "[TreatmentRuntime] return reconciliation failed",
              reconciliationError,
            );
          }
          return null;
        })
        .finally(() => {
          if (treatmentRuntimeReconcileInFlightRef.current === request) {
            treatmentRuntimeReconcileInFlightRef.current = null;
          }
        });
      treatmentRuntimeReconcileInFlightRef.current = request;
      return request;
    };
    const scheduleReconcileOnReturn = () => {
      if (
        document.visibilityState === "hidden"
        || treatmentUploadInFlightRef.current > 0
        || !treatmentAuthorityRef.current
      ) {
        return;
      }
      clearReconcileTimer();
      treatmentRuntimeReconcileTimerRef.current = window.setTimeout(() => {
        treatmentRuntimeReconcileTimerRef.current = null;
        reconcileNow();
      }, 150);
    };
    const reconcileBrowserCache = (event) => {
      if (!event.persisted) return;
      reconcileNow({ pageRestore: true });
    };
    const onRuntimeBroadcast = (event) => {
      const message = event?.data || {};
      const current = treatmentAuthorityRef.current;
      if (
        !current
        || (message.sourceKind && message.sourceKind !== runtimeSourceKind)
        || (message.sourceId && String(message.sourceId) !== String(runtimeSourceId))
        || (message.releaseId && message.releaseId !== current.release?.id)
        || (
          message.releaseChecksum
          && message.releaseChecksum !== current.release?.checksum
        )
        || message.sessionToken !== current.session_token
        || Number(message.revision || -1) <= treatmentRuntimeRevision(current)
      ) {
        return;
      }
      treatmentRuntimeReconcileRef.current = (
        treatmentRuntimeReconcileRef.current
          .catch(() => undefined)
          .then(() => reconcileNow())
      );
    };
    const channel = createTreatmentRuntimeChannel(onRuntimeBroadcast);
    treatmentRuntimeChannelRef.current = channel;
    window.addEventListener("pageshow", reconcileBrowserCache);
    document.addEventListener("visibilitychange", scheduleReconcileOnReturn);
    return () => {
      clearReconcileTimer();
      window.removeEventListener("pageshow", reconcileBrowserCache);
      document.removeEventListener("visibilitychange", scheduleReconcileOnReturn);
      closeTreatmentRuntimeChannel(channel, onRuntimeBroadcast);
      if (treatmentRuntimeChannelRef.current === channel) {
        treatmentRuntimeChannelRef.current = null;
      }
    };
  }, [
    getCurrentQuestion,
    installAuthoritativeTreatmentProjection,
    previewCapabilityToken,
    runtimeSourceId,
    runtimeSourceKind,
  ]);

  const synchronizeTreatmentAuthority = useCallback(
    async (
      targetState,
      responsesOverride = null,
      patientInfoOverride = null,
      options = {},
    ) => {
      if (
        !targetState
        || (!runtimeSourceId && !previewCapabilityToken)
      ) {
        return null;
      }
      const current = treatmentAuthorityRef.current;
      if (!current) {
        throw new Error("Treatment session is still loading.");
      }
      const effectiveResponses = responsesOverride || responsesRef.current;
      const intent = [
        "synchronize",
        current.session_token,
        current.session_revision,
        targetState,
      ].join(":");
      return treatmentAuthorityMutationGateRef.current(intent, async () => {
        const synchronize = (authority, submittedResponses) => synchronizeAuthoritySession({
          session: authority,
          questionnaire,
          responses: submittedResponses,
          patientInfo: patientInfoOverride || patientInfo,
          patientAccessToken: (
            authority.mode === "preview"
              ? null
              : sessionStorage.getItem(TREATMENT_STORAGE_KEY.patientAccessToken)
          ),
          targetState,
          couponCode: (
            sessionStorage.getItem("welliemd_coupon_code") || ""
          ).trim(),
          recommendedProgramIds: options.recommendedProgramIds ?? null,
          allowBackward: options.allowBackward === true,
          backTargetNodeId: options.backTargetNodeId || null,
        });
        let synchronized;
        let authority = current;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            synchronized = await synchronize(authority, effectiveResponses);
            break;
          } catch (error) {
            const canRetry = (
              attempt === 0
              && options.retryOnRevisionConflict !== false
              && ![
                TREATMENT_RUNTIME_STATE.completed,
                TREATMENT_RUNTIME_STATE.checkoutConsumed,
              ].includes(targetState)
              && ["stale_revision", "runtime_revision_stale"].includes(error?.code)
              && error.current
            );
            if (!canRetry) {
              if (
                ["stale_revision", "runtime_revision_stale"].includes(error?.code)
                && error.current
              ) {
                error.code = "runtime_revision_stale";
                error.reconciliationRequired = true;
                error.current = installAuthoritativeTreatmentProjection(
                  error.current,
                  { preservePendingResponses: true },
                );
              }
              throw error;
            }
            authority = installAuthoritativeTreatmentProjection(error.current, {
              preservePendingResponses: true,
            });
          }
        }
        const currentOrder = TREATMENT_RUNTIME_STATE_ORDER[current.state];
        const targetOrder = TREATMENT_RUNTIME_STATE_ORDER[targetState];
        synchronized = installAuthoritativeTreatmentProjection(synchronized, {
          // Forward checkpoints retain later browser-owned answers. Backward
          // transitions normally install only server-canonical answers, but
          // callers may preserve checkout inputs while reopening the form.
          preservePendingResponses: (
            options.preservePendingResponses
            ?? (
              currentOrder === undefined
              || targetOrder === undefined
              || targetOrder >= currentOrder
            )
          ),
        });
        if (
          current.state === TREATMENT_RUNTIME_STATE.checkoutReady
          && synchronized.state !== TREATMENT_RUNTIME_STATE.checkoutReady
        ) {
          treatmentCheckoutRef.current = null;
          treatmentCheckoutAttemptRef.current = null;
          setTreatmentCheckout(null);
          setTreatmentCheckoutRecovery(null);
          setTreatmentQuoteChange(null);
        }
        return synchronized;
      });
    },
    [
      previewCapabilityToken,
      patientInfo,
      questionnaire,
      runtimeSourceId,
      runtimeSourceKind,
      getCurrentQuestion,
      installAuthoritativeTreatmentProjection,
    ],
  );

  const validateTreatmentLocation = useCallback(
    async (responsesOverride = null, { locationRole = "patient_service" } = {}) => {
      const current = treatmentAuthorityRef.current;
      if (!current) {
        throw new Error("Treatment session is unavailable. Please retry.");
      }
      const effectiveResponses = responsesOverride || responsesRef.current;
      const validate = async (candidateResponses) => {
        const payload = buildAuthorityPayload(questionnaire, candidateResponses);
        const isShippingLocation = locationRole === "shipping";
        const shippingQuestion = isShippingLocation
          ? (questionnaire?.questions || []).find((question) => (
            question.question_type === "shipping_address"
            && question.location_role === "shipping"
          ))
          : null;
        const shippingResponseKey = shippingQuestion
          ? [
              shippingQuestion.id,
              shippingQuestion.source_id,
              shippingQuestion.release_node_id,
            ].find((key) => (
              key
              && Object.prototype.hasOwnProperty.call(
                candidateResponses,
                key,
              )
            ))
          : null;
        const location = isShippingLocation
          ? canonicalLocationAnswer(
            shippingQuestion,
            shippingResponseKey
              ? candidateResponses[shippingResponseKey]
              : null,
          )
          : payload.location;
        if (!location) {
          throw new Error(
            `Please complete your ${
              isShippingLocation ? "shipping" : "service"
            } address.`,
          );
        }
        return validateTreatmentSessionLocation(
          current,
          location,
          location?.accepted_correction_fingerprint || null,
          locationRole,
        );
      };
      return validate(effectiveResponses);
    },
    [questionnaire],
  );

  const continueTreatmentWithEligiblePrograms = useCallback(
    async ({
      eligibleProgramIds,
      acknowledgedExclusions,
      availabilityDecisionIds,
    }) => {
      const current = treatmentAuthorityRef.current;
      if (!current) throw new Error("Treatment session is unavailable. Please retry.");
      const intent = [
        "continue_with_eligible_programs",
        current.session_token,
        current.session_revision,
      ].join(":");
      return treatmentAuthorityMutationGateRef.current(intent, async () => {
        try {
          const next = await continueWithEligibleProgramsAuthority({
            session: current,
            eligibleProgramIds,
            acknowledgedExclusions,
            availabilityDecisionIds,
          });
          return installAuthoritativeTreatmentProjection(next);
        } catch (error) {
          if (
            ["stale_revision", "runtime_revision_stale"].includes(error?.code)
            && error.current
          ) {
            error.code = "runtime_revision_stale";
            error.reconciliationRequired = true;
            error.current = installAuthoritativeTreatmentProjection(
              error.current,
              { preservePendingResponses: true },
            );
          }
          throw error;
        }
      });
    }, [installAuthoritativeTreatmentProjection],
  );

  const uploadTreatmentRuntimeFile = useCallback(async (question, file) => {
    const current = treatmentAuthorityRef.current;
    if (!current) throw new Error("Treatment session is unavailable. Please retry.");
    const intent = [
      "upload_file",
      current.session_token,
      current.session_revision,
      String(question.release_node_id || question.source_id || question.id || ""),
    ].join(":");
    return treatmentAuthorityMutationGateRef.current(intent, async () => {
      treatmentUploadInFlightRef.current += 1;
      if (treatmentRuntimeReconcileTimerRef.current !== null) {
        window.clearTimeout(treatmentRuntimeReconcileTimerRef.current);
        treatmentRuntimeReconcileTimerRef.current = null;
      }
      try {
        let authority;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const reconciliation = treatmentRuntimeReconcileInFlightRef.current;
          if (reconciliation) await reconciliation;
          const latest = treatmentAuthorityRef.current;
          if (!latest) {
            throw new Error("Treatment session is unavailable. Please retry.");
          }
          authority = await synchronizeQuestionBoundary({
            session: latest,
            questionnaire,
            responses: responsesRef.current,
            patientInfo,
            patientAccessToken: (
              latest.mode === "preview"
                ? null
                : sessionStorage.getItem(TREATMENT_STORAGE_KEY.patientAccessToken)
            ),
            couponCode: (
              sessionStorage.getItem("welliemd_coupon_code") || ""
            ).trim(),
          }, question);
          authority = installAuthoritativeTreatmentProjection(authority, {
            preservePendingResponses: true,
          });
          const expectedState = authorityStateForQuestion(question);
          if (authority.state !== expectedState) {
            throw new Error(
              "This upload question is not available at the current step.",
            );
          }
          try {
            const receipt = await uploadRuntimeFile(authority, question, file);
            const persistedRevision = Number(
              receipt?.runtime_session_revision,
            );
            if (
              Number.isInteger(persistedRevision)
              && persistedRevision >= Number(authority.session_revision || 0)
            ) {
              const revisedAuthority = {
                ...authority,
                session_revision: persistedRevision,
              };
              setTreatmentAuthorityRef(revisedAuthority);
              if (previewCapabilityToken) {
                persistTreatmentPreviewSession(
                  previewCapabilityToken,
                  revisedAuthority,
                );
              } else {
                persistTreatmentAuthority(revisedAuthority, {
                  sourceKind: runtimeSourceKind,
                  sourceId: runtimeSourceId,
                  currentItemId: (
                    question.release_node_id || question.source_id || question.id
                  ),
                });
              }
            }
            return receipt;
          } catch (error) {
            if (
              attempt === 0
              && ["stale_revision", "runtime_revision_stale"].includes(error?.code)
              && error.current
            ) {
              installAuthoritativeTreatmentProjection(error.current, {
                preservePendingResponses: true,
              });
              continue;
            }
            if (
              ["stale_revision", "runtime_revision_stale"].includes(error?.code)
              && error.current
            ) {
              installAuthoritativeTreatmentProjection(error.current, {
                preservePendingResponses: true,
              });
            }
            throw error;
          }
        }
      } finally {
        treatmentUploadInFlightRef.current = Math.max(
          0,
          treatmentUploadInFlightRef.current - 1,
        );
      }
    });
  }, [
    installAuthoritativeTreatmentProjection,
    patientInfo,
    previewCapabilityToken,
    questionnaire,
    runtimeSourceId,
    runtimeSourceKind,
  ]);

  const removeQuestionnaireFile = useCallback(async (question, receipt) => {
    const current = treatmentAuthorityRef.current;
    if (current) {
      const intent = [
        "remove_upload",
        current.session_token,
        current.session_revision,
        String(question.release_node_id || question.source_id || question.id || ""),
        String(receipt?.upload_reference || ""),
      ].join(":");
      return treatmentAuthorityMutationGateRef.current(intent, async () => {
        try {
          const result = await removeRuntimeFile(current, question, receipt);
          const revision = Number(result?.runtime_session_revision);
          const revisedAuthority = Number.isInteger(revision)
            ? { ...current, session_revision: revision }
            : current;
          setTreatmentAuthorityRef(revisedAuthority);
          if (previewCapabilityToken) {
            persistTreatmentPreviewSession(
              previewCapabilityToken,
              revisedAuthority,
            );
          } else {
            persistTreatmentAuthority(revisedAuthority, {
              sourceKind: runtimeSourceKind,
              sourceId: runtimeSourceId,
              currentItemId: (
                question.release_node_id || question.source_id || question.id
              ),
            });
          }
          return result;
        } catch (error) {
          if (
            ["stale_revision", "runtime_revision_stale"].includes(error?.code)
            && error.current
          ) {
            installAuthoritativeTreatmentProjection(error.current, {
              preservePendingResponses: true,
            });
            error.message = (
              "Your questionnaire progress changed. Refresh and try removing the file again."
            );
          }
          throw error;
        }
      });
    }

    const nextResponses = { ...responsesRef.current };
    delete nextResponses[question.id];
    const sanitizedResponses = sanitizeDraftResponses(nextResponses);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    if (currentToken.current) {
      await queueDraftSave(
        () => api.saveQuestionnaireDraft(
          currentToken.current,
          sanitizedResponses,
          currentQuestionIndex,
          questionnaire?.version,
        ),
      );
    }
    if (!disableLocalDraft) {
      await saveDraftToStorage(activeDraftKeyRef.current, {
        templateId,
        templateVersion: questionnaire?.version,
        answers: nextResponses,
        current_question_index: currentQuestionIndex,
        updatedAt: Date.now(),
        patientInfo: patientInfo
          ? {
              patient_id: patientInfo.patient_id,
              email: patientInfo.email,
              phone: patientInfo.phone,
              is_new_patient: patientInfo.is_new_patient,
            }
          : null,
      });
    }
    return { removed: true };
  }, [
    currentQuestionIndex,
    disableLocalDraft,
    installAuthoritativeTreatmentProjection,
    patientInfo,
    previewCapabilityToken,
    questionnaire,
    queueDraftSave,
    runtimeSourceId,
    runtimeSourceKind,
    templateId,
  ]);

  const checkpointTreatmentProgramAnswer = useCallback(
    async (question, value, responsesOverride = null) => {
      const current = treatmentAuthorityRef.current;
      if (!current) {
        throw new Error("Treatment session is still loading.");
      }
      if (!requiresProgramAnswerCheckpoint(
        question,
        current.presentation_contract_version,
      )) return null;
      const sourceKind = String(
        current.release?.source_kind
        || current.source_kind
        || runtimeSourceKind
        || "",
      );
      if (![
        TREATMENT_SOURCE_KIND.program,
        TREATMENT_SOURCE_KIND.customProgram,
      ].includes(sourceKind)) return null;
      if (
        current.presentation_contract_version === "1"
        && (
          !question?.program_id
          || String(question?.question_type || question?.kind || "")
            .trim().toLowerCase() === "state_routing"
        )
        && [
          TREATMENT_RUNTIME_STATE.routing,
          TREATMENT_RUNTIME_STATE.commonScreening,
        ].includes(current.state)
      ) {
        const payload = buildBoundaryAnswerCheckpoint(question, value);
        if (!payload) throw new Error("This intake answer cannot be checkpointed.");
        const action = current.state === TREATMENT_RUNTIME_STATE.routing
          ? TREATMENT_RUNTIME_ACTION.checkpointRoutingAnswer
          : TREATMENT_RUNTIME_ACTION.checkpointCommonAnswer;
        const intent = [
          action,
          current.session_token,
          current.session_revision,
          payload.checkpoint_source_id,
        ].join(":");
        return treatmentAuthorityMutationGateRef.current(intent, async () => {
          const checkpointed = await transitionTreatmentSession(
            current,
            action,
            payload,
          );
          return installAuthoritativeTreatmentProjection(checkpointed, {
            preservePendingResponses: true,
          });
        });
      }
      // Custom Program questions authored in Stage 1 (including questions
      // carrying disqualifying-option metadata) are common-flow inputs. They
      // do not have a program_id and therefore cannot be represented by the
      // Program-only checkpoint contract. They are submitted with the normal
      // Custom Program boundary transition instead.
      if (
        sourceKind === TREATMENT_SOURCE_KIND.customProgram
        && !question?.program_id
      ) return null;
      const effectiveRuntimeProgramId = String(
        question?.program_id
        || runtimeProgramId
        || current.source_id
        || current.release?.source_id
        || "",
      );
      // Only visible upstream local answers belong in this question-scoped
      // checkpoint. The runtime session already retains accepted answers.
      const locallyRetainedProgramAnswers = (
        question?.program_id
          ? buildProgramAnswerContext(
            questionnaire,
            responsesOverride || responsesRef.current,
            question.program_id,
            question,
          )
          : {}
      );
      const programAnswers = (
        question?.program_id
      ) ? locallyRetainedProgramAnswers : null;
      const effectiveValue = value !== undefined
        ? value
        : (responsesOverride || responsesRef.current)?.[question?.id]
          ?? (responsesOverride || responsesRef.current)?.[question?.source_id]
          ?? (responsesOverride || responsesRef.current)?.[question?.release_node_id];
      let payload = buildProgramAnswerCheckpoint(
        question,
        effectiveValue,
        programAnswers,
        checkpointProgramIdentity(sourceKind, effectiveRuntimeProgramId),
      );
      if (!payload) return null;
      const intent = [
        "checkpoint_program_answer",
        current.session_token,
        current.session_revision,
        String(question.source_id || question.id || ""),
        JSON.stringify(payload),
      ].join(":");
      return treatmentAuthorityMutationGateRef.current(intent, async () => {
        let checkpointAuthority = current;
        let checkpointed;
        try {
          checkpointAuthority = await synchronizeProgramCheckpointBoundary({
            session: current,
            questionnaire,
            responses: responsesRef.current,
            patientInfo,
            patientAccessToken: (
              current.mode === "preview"
                ? null
                : sessionStorage.getItem(
                  TREATMENT_STORAGE_KEY.patientAccessToken,
                )
            ),
            couponCode: (
              sessionStorage.getItem("welliemd_coupon_code") || ""
            ).trim(),
          });
          checkpointAuthority = installAuthoritativeTreatmentProjection(
            checkpointAuthority,
            { preservePendingResponses: true },
          );
          if (
            checkpointAuthority.state
            !== TREATMENT_RUNTIME_STATE.treatmentScreening
          ) {
            return checkpointAuthority;
          }
          checkpointed = await transitionTreatmentSession(
            checkpointAuthority,
            TREATMENT_RUNTIME_ACTION.checkpointProgramAnswers,
            payload,
          );
        } catch (error) {
          const hiddenAnswerRecovery = recoverHiddenAnswerCheckpoint({
            questionnaire,
            responses: responsesRef.current,
            payload,
            currentQuestion: question,
            error,
          });
          if (hiddenAnswerRecovery) {
            responsesRef.current = hiddenAnswerRecovery.responses;
            setResponses(hiddenAnswerRecovery.responses);
            payload = hiddenAnswerRecovery.payload;
            if (hiddenAnswerRecovery.canRetry) {
              checkpointed = await transitionTreatmentSession(
                checkpointAuthority,
                TREATMENT_RUNTIME_ACTION.checkpointProgramAnswers,
                payload,
              );
            } else {
              throw error;
            }
          } else if (
            ["stale_revision", "runtime_revision_stale"].includes(error?.code)
            && error.current
          ) {
            error.code = "runtime_revision_stale";
            error.reconciliationRequired = true;
            error.current = installAuthoritativeTreatmentProjection(
              error.current,
              { preservePendingResponses: true },
            );
            throw error;
          } else {
            throw error;
          }
        }
        return installAuthoritativeTreatmentProjection(checkpointed, {
          preservePendingResponses: true,
        });
      });
    },
    [
      installAuthoritativeTreatmentProjection,
      patientInfo,
      questionnaire,
      runtimeSourceKind,
      runtimeProgramId,
    ],
  );

  const bindTreatmentPatientAuthority = useCallback(
    async (patientAccessToken, { refreshProfile = false } = {}) => {
      const current = treatmentAuthorityRef.current;
      if (!current) {
        throw new Error("Treatment session is still loading.");
      }
      if (current.mode === "preview") return current;
      if (!runtimeSourceId) return null;
      const intent = [
        "bind_patient",
        current.session_token,
        current.session_revision,
      ].join(":");
      return treatmentAuthorityMutationGateRef.current(intent, async () => {
        if (
          current.mode === "preview"
          || (current.patient_bound && !refreshProfile)
        ) {
          return current;
        }
        let bound;
        try {
          bound = await bindTreatmentPatient(current, patientAccessToken, {});
        } catch (error) {
          if (
            ["stale_revision", "runtime_revision_stale"].includes(error?.code)
            && error.current
          ) {
            error.code = "runtime_revision_stale";
            error.reconciliationRequired = true;
            const reconciled = installAuthoritativeTreatmentProjection(
              error.current,
              { preservePendingResponses: true },
            );
            error.current = reconciled;
            if (reconciled.patient_bound) return reconciled;
          }
          throw error;
        }
        return installAuthoritativeTreatmentProjection(bound, {
          preservePendingResponses: true,
        });
      });
    },
    [installAuthoritativeTreatmentProjection, runtimeSourceId],
  );

  const nextQuestion = useCallback((responsesOverride, currentQuestionId = null) => {
    // Always compute fresh visibility from the explicit override or the latest
    // committed responses.  This avoids stale-closure bugs when the call
    // originates from a setTimeout (auto-advance after answer change).
    const effectiveResponses = responsesOverride || responsesRef.current;
    const visibleQuestions = computeVisibleQuestions(effectiveResponses);
    const nextIndex = currentQuestionId
      ? resolveForwardNodeIndex({
          allQuestions: hasTreatmentAuthority
            ? orderQuestionsByRuntimeBoundary(questionnaire?.questions)
            : questionnaire?.questions,
          visibleQuestions,
          currentNodeId: currentQuestionId,
        })
      : (
          currentQuestionIndex < visibleQuestions.length - 1
            ? currentQuestionIndex + 1
            : -1
        );

    if (nextIndex >= 0) {
      setCurrentQuestionIndex(nextIndex);
      setQuestionStartTime(Date.now());
      flushDraftToStorageRef.current?.(effectiveResponses, nextIndex);
      return true;
    }
    // Reported so callers can tell "moved on" from "there was nowhere to go"
    // and fall back to their own handling instead of stalling silently.
    return false;
  }, [currentQuestionIndex, computeVisibleQuestions, questionnaire?.questions, hasTreatmentAuthority]);

  const navigateToTreatmentAuthorityState = useCallback((targetState) => {
    if (!targetState) return false;
    const visibleQuestions = computeVisibleQuestions(responsesRef.current);
    const targetIndex = visibleQuestions.findIndex(
      (question) => authorityStateForQuestion(question) === targetState,
    );
    if (targetIndex < 0) return false;
    setCurrentQuestionIndex(targetIndex);
    setQuestionStartTime(Date.now());
    flushDraftToStorageRef.current?.(responsesRef.current, targetIndex);
    return true;
  }, [computeVisibleQuestions]);

  const navigateToCurrentTreatmentAuthority = useCallback(({
    preferTreatmentIntro = false,
    excludedQuestionTypes = [],
  } = {}) => {
    const projection = treatmentAuthorityRef.current;
    if (!projection || !questionnaire?.questions) return false;
    const visibleQuestions = computeVisibleQuestions(responsesRef.current);
    const shouldStartAtTreatmentIntro = (
      projection.state === TREATMENT_RUNTIME_STATE.treatmentScreening
      && preferTreatmentIntro
    );
    const boundaryStartIndex = shouldStartAtTreatmentIntro
      ? firstVisibleNodeIndexForState({
          visibleQuestions,
          targetState: projection.state,
          stateForQuestion: authorityStateForQuestion,
        })
      : -1;
    const targetIndex = boundaryStartIndex >= 0
      ? boundaryStartIndex
      : resolveAuthoritativeNodeIndex({
          allQuestions: hasTreatmentAuthority
            ? orderQuestionsByRuntimeBoundary(questionnaire.questions)
            : questionnaire.questions,
          visibleQuestions,
          browserNodeId: null,
          projection,
          stateForQuestion: authorityStateForQuestion,
        });
    const targetQuestion = visibleQuestions[targetIndex];
    if (
      targetIndex < 0
      || !targetQuestion
      || excludedQuestionTypes.includes(targetQuestion.question_type)
    ) return false;
    if (preferTreatmentIntro) pendingStableNavigationRef.current = null;
    setCurrentQuestionIndex(targetIndex);
    setQuestionStartTime(Date.now());
    flushDraftToStorageRef.current?.(responsesRef.current, targetIndex);
    return true;
  }, [computeVisibleQuestions, questionnaire?.questions, hasTreatmentAuthority]);

  const previousQuestion = useCallback(() => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      setQuestionStartTime(Date.now());
      flushDraftToStorageRef.current?.(responsesRef.current, prevIndex);
    }
  }, [currentQuestionIndex]);

  const installAuthoritativeTreatmentProjectionRef = useRef(installAuthoritativeTreatmentProjection);
  useEffect(() => {
    installAuthoritativeTreatmentProjectionRef.current = installAuthoritativeTreatmentProjection;
  }, [installAuthoritativeTreatmentProjection]);

  const resetTreatmentPreviewAuthority = useCallback(async () => {
    if (!previewCapabilityToken) return null;
    clearTreatmentPreviewSession();
    if (!questionnaire) {
      // No questionnaire ever loaded (e.g. the initial preview load failed
      // before reaching setQuestionnaire) — a plain session reset would leave
      // nothing to render. Re-run full initialization instead, now that the
      // stale session pointer has been cleared.
      return initialize();
    }
    setLoading(true);
    setError(null);
    try {
      const session = await startTreatmentPreviewAuthority(
        previewCapabilityToken,
        previewApiBaseUrl,
        {
          forceNew: true,
          snapshotId: previewSnapshotId,
          snapshotChecksum: previewSnapshotChecksum,
          previewPatient: previewPatientDefaults(questionnaire),
        },
      );
      setTreatmentAuthorityRef(session);
      treatmentCheckoutRef.current = null;
      treatmentCheckoutAttemptRef.current = null;
      resumeCursorRef.current = null;
      responsesRef.current = {};
      setResponses({});
      setTreatmentCheckout(null);
      setTreatmentCheckoutRecovery(null);
      setTreatmentQuoteChange(null);
      setCompleted(false);
      setCurrentQuestionIndex(0);
      installAuthoritativeTreatmentProjectionRef.current(session, {
        preservePreviewAuthentication: true,
      });
      setTreatmentRuntimeError(null);
      setError(null);
      return session;
    } catch (resetError) {
      setError(
        getApiErrorMessage(resetError)
        || "Failed to reset treatment preview.",
      );
      throw resetError;
    } finally {
      setLoading(false);
    }
  }, [
    initialize,
    previewApiBaseUrl,
    previewCapabilityToken,
    previewSnapshotChecksum,
    previewSnapshotId,
    questionnaire,
  ]);

  const resetTreatmentPatientAuthority = useCallback(async () => {
    if (!runtimeSourceId) return null;
    clearTreatmentAuthority();
    setTreatmentAuthorityRef(null);
    treatmentCheckoutRef.current = null;
    treatmentCheckoutAttemptRef.current = null;
    setTreatmentCheckout(null);
    setTreatmentCheckoutRecovery(null);
    setTreatmentQuoteChange(null);
    await initialize();
    return treatmentAuthorityRef.current;
  }, [initialize, runtimeSourceId]);

  const rewindTreatmentResponses = useCallback((questionIdentity) => {
    if (
      (!runtimeSourceId && !previewCapabilityToken)
      || !questionIdentity
      || !questionnaire
    ) {
      return responsesRef.current;
    }
    const questions = Array.isArray(questionnaire.questions)
      ? questionnaire.questions
      : [];
    const targetIndex = questions.findIndex((question) => (
      [question.id, question.source_id, question.release_node_id]
        .filter(Boolean)
        .some((value) => String(value) === String(questionIdentity))
    ));
    if (targetIndex < 0) return responsesRef.current;

    const nextResponses = { ...responsesRef.current };
    questions.slice(targetIndex).forEach((question) => {
      delete nextResponses[question.id];
    });
    responsesRef.current = nextResponses;
    setResponses(nextResponses);
    if (treatmentAuthorityRef.current && runtimeSourceId) {
      persistTreatmentAuthority(treatmentAuthorityRef.current, {
        sourceKind: runtimeSourceKind,
        sourceId: runtimeSourceId,
        responses: nextResponses,
        currentItemId: (
          questions[targetIndex].release_node_id
          || questions[targetIndex].id
        ),
      });
    }
    return nextResponses;
  }, [
    previewCapabilityToken,
    questionnaire,
    runtimeSourceId,
    runtimeSourceKind,
  ]);

  const isLastQuestion = useCallback(() => {
    const visibleQuestions = computeVisibleQuestions(responsesRef.current);
    return currentQuestionIndex === visibleQuestions.length - 1;
  }, [currentQuestionIndex, computeVisibleQuestions]);

  const complete = useCallback(
    async (patientData, paymentData = null, responsesOverride = null) => {
      if (!questionnaire) throw new Error("No active questionnaire");

      setLoading(true);
      setError(null);

      try {
        const latestResponses = responsesOverride || responsesRef.current;

        if (isPreviewMode && previewCapabilityToken) {
          let previewSession = await prepareTreatmentCheckout({
            session: treatmentAuthorityRef.current,
            questionnaire,
            responses: latestResponses,
            patientAccessToken: null,
            couponCode: "",
          });
          setTreatmentAuthorityRef(previewSession);
          if (paymentData) {
            previewSession = await transitionTreatmentSession(
              previewSession,
              TREATMENT_RUNTIME_ACTION.completePreview,
              {},
            );
            setTreatmentAuthorityRef(previewSession);
            setCompleted(true);
            return previewSession;
          }
          setTreatmentCheckout({
            ...previewSession,
            order_summary: buildLiveOrderSummary(previewSession),
          });
          return {
            success: true,
            payment_required: true,
            checkout_ready: previewSession,
          };
        }
        if (isPreviewMode) {
          const capabilityError = new Error(
            "A preview capability is required to run this treatment preview.",
          );
          capabilityError.code = "preview_capability_required";
          throw capabilityError;
        }
        journeyBoundary.effectAdapter.assertAllowed(
          JOURNEY_PROTECTED_EFFECT.completion,
        );

        // Live program/custom-program journeys must first prepare the
        // release-bound checkout capability. The final consent step has no
        // payment payload yet; falling through to the legacy order endpoint
        // loses the runtime patient identity and is rejected for Phase II
        // multi-treatment submissions.
        if (runtimeSourceId && !paymentData) {
          const preparedCheckout = await prepareTreatmentCheckout({
            session: treatmentAuthorityRef.current,
            questionnaire,
            responses: latestResponses,
            patientAccessToken: sessionStorage.getItem(
              TREATMENT_STORAGE_KEY.patientAccessToken,
            ),
            couponCode: sessionStorage.getItem("welliemd_coupon_code") || "",
          });
          const checkout = ensureCheckoutClientIdempotencyKey(preparedCheckout);
          setTreatmentAuthorityRef(checkout);
          treatmentCheckoutRef.current = checkout;
          treatmentCheckoutAttemptRef.current = checkout.client_idempotency_key;
          persistTreatmentCheckoutResume(checkout, {
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
            idempotencyKey: checkout.client_idempotency_key,
            recovery: null,
          });
          setTreatmentCheckout({
            ...checkout,
            order_summary: buildLiveOrderSummary(checkout),
          });
          return {
            success: true,
            payment_required: true,
            checkout_ready: checkout,
          };
        }

        if (runtimeSourceId && paymentData) {
          const checkout = treatmentCheckoutRef.current;
          if (!checkout?.checkout_capability) {
            throw new Error("Secure treatment checkout is not ready.");
          }
          const token = paymentMethodToken(paymentData);
          const paymentMethod = normalizePaymentMethod(paymentData);
          if (!token || !paymentMethod) throw new Error("Payment method is required.");
          if (!treatmentCheckoutAttemptRef.current) {
            treatmentCheckoutAttemptRef.current = typeof crypto !== "undefined" && crypto.randomUUID
              ? crypto.randomUUID()
              : `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;
          }
          if (treatmentCheckoutSubmitInFlightRef.current) {
            return treatmentCheckoutSubmitInFlightRef.current;
          }
          const selectedProducts = buildAuthorityPayload(
            questionnaire,
            latestResponses,
          ).selections;
          const submission = submitTreatmentCheckout({
            checkoutCapability: checkout.checkout_capability,
            runtimeSessionRevision: checkout.session_revision,
            idempotencyKey: treatmentCheckoutAttemptRef.current,
            paymentMethodToken: token,
            paymentMethod,
            selectedProducts,
            labCheckout: (() => {
              const question = (questionnaire.questions || []).find(
                (item) => item.question_type === "program_lab_checkout",
              );
              if (!question) return null;
              const labCheckout = {
                treatments: (question.program_labs || []).map((program) => ({
                  program_id: program.program_id,
                  program_name: program.program_name,
                  labs: program.labs || [],
                })),
              };
              return normalizeProgramLabCheckout(
                latestResponses[question.id],
                labCheckout,
              );
            })(),
            apiBaseUrl: checkout.runtime_api_base_url || runtimeApiBaseUrl,
          }).then((result) => {
            // A response with orders is still useful to the patient when an
            // operation is queued or its payment status is unknown. The
            // result screen renders the per-treatment state and keeps the
            // already-created checkout visible. Empty responses remain hard
            // failures so a missing checkout cannot look complete.
            if (!Array.isArray(result?.orders) || result.orders.length === 0) {
              assertCombinedCheckoutSuccess(result);
            }
            clearTreatmentAuthority();
            setCompleted(true);
            return {
              ...result,
              success: isCombinedCheckoutSuccessful(result),
              patient_id: checkout.patient_id,
            };
          });
          treatmentCheckoutSubmitInFlightRef.current = submission;
          try {
            return await submission;
          } finally {
            if (treatmentCheckoutSubmitInFlightRef.current === submission) {
              treatmentCheckoutSubmitInFlightRef.current = null;
            }
          }
        }

        // 🆕 Extract patient info from responses: first from grouped questions (personal_details, shipping_address), then from top-level beluga_field_mapping
        const extractedPatientInfo = {};

        // Helper to set a single patient info field from a response value
        const setPatientInfoField = (mapping, value, target) => {
          if (value === undefined || value === null || value === "") return;
          switch (mapping) {
            case "first_name":
              target.firstName = value;
              break;
            case "last_name":
              target.lastName = value;
              break;
            case "date_of_birth":
              target.dob = value;
              if (value) {
                try {
                  let birthDate;
                  if (typeof value === "string" && value.includes("/")) {
                    const [month, day, year] = value.split("/");
                    birthDate = new Date(year, month - 1, day);
                  } else if (typeof value === "string" && value.includes("-")) {
                    birthDate = new Date(value);
                  } else {
                    birthDate = new Date(value);
                  }
                  if (birthDate && !isNaN(birthDate.getTime())) {
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (
                      monthDiff < 0 ||
                      (monthDiff === 0 && today.getDate() < birthDate.getDate())
                    ) {
                      age--;
                    }
                    target.age = age;
                  }
                } catch (err) {
                  console.error("Error calculating age:", err);
                }
              }
              break;
            case "phone_number":
              target.phone = value;
              break;
            case "email":
              target.email = value;
              break;
            case "street_address":
              target.address = value;
              break;
            case "city":
              target.city = value;
              break;
            case "state":
              target.state = value;
              break;
            case "zip_code":
              target.zip = value;
              break;
            case "sex":
              target.sex = value;
              break;
            default:
              if (mapping) target[mapping] = value;
              break;
          }
        };

        // 1) Extract from grouped questions (personal_details, shipping_address) — sub-questions hold email/phone etc.
        questionnaire.questions.forEach((question) => {
          if (
            question.question_type !== "personal_details" &&
            question.question_type !== "shipping_address"
          ) {
            return;
          }
          const groupResponse = latestResponses[question.id];
          if (!groupResponse || typeof groupResponse !== "object") return;

          const subQuestions = question.sub_questions || [];
          subQuestions.forEach((subQ) => {
            const rawValue = groupResponse[subQ.id];
            if (rawValue === undefined || rawValue === null) return;
            const value =
              typeof rawValue === "string" ? rawValue.trim() : rawValue;
            if (value === "") return;

            const questionTextLower = subQ.question_text?.toLowerCase() || "";
            const mapping =
              subQ.beluga_field_mapping ||
              (questionTextLower.includes("email") && "email") ||
              (questionTextLower.includes("phone") && "phone_number") ||
              (questionTextLower.includes("first name") && "first_name") ||
              (questionTextLower.includes("last name") && "last_name") ||
              (questionTextLower.includes("date of birth") && "date_of_birth") ||
              ((questionTextLower.includes("apartment") || questionTextLower.includes("suite") || questionTextLower.includes("unit") || questionTextLower.includes("apt") || questionTextLower.includes("address line 2") || questionTextLower.includes("address 2")) && "address2") ||
              (questionTextLower.includes("address") && "street_address") ||
              (questionTextLower.includes("city") && "city") ||
              (questionTextLower.includes("state") && "state") ||
              (questionTextLower.includes("zip") && "zip_code") ||
              (subQ.question_type === "email" && "email") ||
              (subQ.question_type === "phone" && "phone_number") ||
              null;
            if (mapping) {
              setPatientInfoField(mapping, value, extractedPatientInfo);
            }
          });
        });

        // 2) Top-level questions with beluga_field_mapping (overwrites grouped if both present)
        questionnaire.questions.forEach((question) => {
          const mapping = question.beluga_field_mapping || inferPatientInfoMapping(question);
          const response = latestResponses[question.id];

          if (
            mapping &&
            response !== undefined &&
            response !== null &&
            response !== ""
          ) {
            const value =
              typeof response === "string" ? response.trim() : response;
            setPatientInfoField(mapping, value, extractedPatientInfo);
          }
        });

        // Construct full name if we have first and last name
        if (extractedPatientInfo.firstName && extractedPatientInfo.lastName) {
          extractedPatientInfo.name = `${extractedPatientInfo.firstName} ${extractedPatientInfo.lastName}`;
        }

        // Use extracted patient info if no patientData was provided
        const finalPatientInfo = patientData || extractedPatientInfo;

        console.log(
          "📋 Extracted patient info from responses:",
          extractedPatientInfo
        );
        console.log("📋 Final patient info being sent:", finalPatientInfo);

        const orderQuestionnaireAnswers = buildOrderQuestionnaireAnswers(
          questionnaire.questions,
          latestResponses
        );

        console.log(
          "📤 Order questionnaire answers being sent to backend:",
          orderQuestionnaireAnswers
        );
        console.log("📊 Total responses:", Object.keys(latestResponses).length);
        console.log(
          "📊 Order questionnaire answers:",
          Object.keys(orderQuestionnaireAnswers).length
        );

        const totalCompletionTime = Date.now() - sessionStartTime;

        // 🚀 NEW CHECKOUT FLOW: Use new onboarding and order creation APIs
        try {
          console.log('🚀 Starting new checkout flow...');

          // Check if we already have patient info from onboarding step
          let resolvedPatientId = (
            finalPatientInfo.patient_id
            || finalPatientInfo.patientId
            || patientId
            || treatmentAuthorityRef.current?.patient_id
            || treatmentAuthorityRef.current?.patientId
            || treatmentAuthorityRef.current?.patient?.id
            || null
          );
          const hasBoundRuntimePatient = Boolean(
            runtimeSourceId
            && treatmentAuthorityRef.current?.patient_bound
            && resolvedPatientId,
          );

          // A patient-bound flag is not itself a patient identifier. Only
          // skip onboarding when an actual ID has been resolved.
          if (!resolvedPatientId) {
            // Guard: require at least email or phone before calling onboarding API
            const emailValue = (
              finalPatientInfo.email
              || finalPatientInfo.email_address
            );
            const phoneValue = (
              finalPatientInfo.phone
              || finalPatientInfo.phone_number
            );
            const email = emailValue?.trim?.() || emailValue;
            const phone = phoneValue?.trim?.() || phoneValue;
            if (!email && !phone) {
              const msg =
                "We couldn't find your email or phone from your saved answers. Please return to the personal details step and re-enter your contact information.";
              setError(msg);
              throw new Error(msg);
            }
            // Fallback: Patient onboarding (if not done during personal_details step)
            console.log('🚀 Step 1: Patient onboarding (fallback)');
            const onboardingResult = await api.onboardPatient(email, phone || null, {
              first_name: finalPatientInfo.firstName || finalPatientInfo.first_name || extractedPatientInfo.firstName || undefined,
              last_name: finalPatientInfo.lastName || finalPatientInfo.last_name || extractedPatientInfo.lastName || undefined,
              template_id: templateId || undefined,
              issue_patient_access_token: Boolean(runtimeSourceId),
            });

            console.log('✅ Patient onboarded:', onboardingResult);
            resolvedPatientId = (
              onboardingResult.patient_id
              || onboardingResult.patientId
              || onboardingResult.patient?.id
              || onboardingResult.id
              || null
            );
          } else {
            console.log(
              hasBoundRuntimePatient
                ? "✅ Using Patient bound to the treatment runtime session"
                : `✅ Using existing patient ID from onboarding: ${patientId}`,
            );
          }

          // Live runtime journeys use the same tokenized checkout URL as the
          // established order flow. Do not stop at the secure inline quote
          // capability here: that path renders the legacy/embedded payment
          // component and never produces the `/checkout/{token}` URL needed
          // by the standalone checkout page. The selected medicines are
          // collected below from the authoritative visible questions and are
          // passed to createOrder, including multi-treatment selections.
          if (!resolvedPatientId) {
            throw new Error(
              "Unable to resolve the patient identity for checkout. Please restart this questionnaire and try again.",
            );
          }

          // Step 2: Extract selected medicines from responses
          const selectedMedicines = [];

          console.log('🔍 Searching for medicines in responses...');
          console.log('📋 Available questions:', questionnaire.questions.map(q => ({ id: q.id, type: q.question_type, text: q.question_text })));
          console.log('📋 All responses:', latestResponses);
          console.log('📋 Order questionnaire answers:', orderQuestionnaireAnswers);

          // Check only responses from VISIBLE questions for medicine selections.
          // This ensures that products from questions that were conditionally hidden
          // (e.g. because the user went back and changed a previous answer) are not included.
          const visibleQuestions = getVisibleQuestions();
          const allResponsesToCheck = {};
          visibleQuestions.forEach(q => {
            if (responsesRef.current[q.id] !== undefined) {
              allResponsesToCheck[q.id] = responsesRef.current[q.id];
            }
          });

          // Adjacent product questions are rendered as one synthetic grouped
          // question. Its response is stored under grouped-product-selection-*
          // instead of the original question ids, so include it in the order
          // extraction as well.
          const productQuestions = visibleQuestions.filter(
            (q) => q.question_type === 'product_selection',
          );
          if (productQuestions.length > 0) {
            const groupedResponseEntry = Object.entries(responsesRef.current).find(
              ([responseId, response]) => (
                responseId.startsWith('grouped-product-selection-')
                && response !== null
                && response !== undefined
                && response !== ''
              ),
            );
            if (groupedResponseEntry) {
              const [responseId, response] = groupedResponseEntry;
              allResponsesToCheck[productQuestions[0].id] = response;
              console.log('✅ Including grouped product-selection response:', responseId);
            }
          }

          Object.entries(allResponsesToCheck).forEach(([questionId, response]) => {
            const question = questionnaire.questions.find(q => q.id === questionId);

            if (!question) {
              console.log(`⚠️ No question found for ID: ${questionId}`);
              return;
            }

            console.log(`🔍 Checking question ${questionId} (${question.question_type}):`, response);

            if (question.question_type === 'product_selection' && response) {
              const productChoices = Array.isArray(question.answer_choices)
                ? question.answer_choices
                : (Array.isArray(question.checkout_config?.products) ? question.checkout_config.products : []);
              const choiceKey = (item) => {
                if (!item || typeof item !== 'object') return item;
                return item.value || item.option_id || item.id || item.product_id || item.label;
              };
              const resolveSelectedProduct = (item) => {
                if (!item) return null;
                const lookupValue = typeof item === 'object'
                  ? (item.value || item.option_id || item.id || item.product_id)
                  : item;
                const matchedChoice = productChoices.find(
                  (choice) => String(choiceKey(choice)) === String(lookupValue)
                );
                const source = matchedChoice || item;
                if (!source || typeof source !== 'object') {
                  return {
                    productId: source,
                    productName: undefined,
                    quantity: 1,
                    optionId: undefined,
                    sourceProductId: undefined,
                    treatmentTypeId: undefined,
                    treatmentTypeKey: undefined,
                  };
                }
                return {
                  productId: source.product_id || source.productId || source.value || source.id,
                  productName: source.product_name || source.productName || source.name || source.label,
                  quantity: source.quantity || source.default_quantity || 1,
                  optionId: source.option_id || source.optionId || source.id,
                  sourceProductId: source.source_product_id || source.sourceProductId,
                  treatmentTypeId: source.treatment_type_id || source.treatmentTypeId,
                  treatmentTypeKey: source.treatment_type_key || source.treatmentTypeKey,
                };
              };
              // Handle product selection responses
              if (Array.isArray(response)) {
                response.forEach((item) => {
                  if (!item) return;
                  const {
                    productId,
                    productName,
                    quantity,
                    optionId,
                    sourceProductId,
                    treatmentTypeId,
                    treatmentTypeKey,
                  } = resolveSelectedProduct(item) || {};
                  if (productId) {
                    selectedMedicines.push({
                      id: productId,
                      quantity: quantity,
                      name: productName,
                      option_id: optionId,
                      source_product_id: sourceProductId,
                      treatment_type_id: treatmentTypeId,
                      treatment_type_key: treatmentTypeKey,
                      question_id: questionId,
                    });
                    console.log('✅ Found product_selection item:', {
                      productId,
                      productName,
                      quantity,
                      optionId,
                      treatmentTypeId,
                      treatmentTypeKey,
                    });
                  }
                });
              } else if (typeof response === 'object') {
                // Product objects can have product_id, id, product_name, name, etc.
                const {
                  productId,
                  productName,
                  quantity,
                  optionId,
                  sourceProductId,
                  treatmentTypeId,
                  treatmentTypeKey,
                } = resolveSelectedProduct(response) || {};

                if (productId) {
                  selectedMedicines.push({
                    id: productId,
                    quantity: quantity,
                    name: productName,
                    option_id: optionId,
                    source_product_id: sourceProductId,
                    treatment_type_id: treatmentTypeId,
                    treatment_type_key: treatmentTypeKey,
                    question_id: questionId,
                  });
                  console.log('✅ Found product_selection:', {
                    productId,
                    productName,
                    quantity,
                    optionId,
                    treatmentTypeId,
                    treatmentTypeKey,
                  });
                }
              } else if (typeof response === 'string') {
                // Handle string responses (fallback)
                const {
                  productId,
                  productName,
                  quantity,
                  optionId,
                  sourceProductId,
                  treatmentTypeId,
                  treatmentTypeKey,
                } = resolveSelectedProduct(response) || {};
                if (productId) {
                  selectedMedicines.push({
                    id: productId,
                    quantity: quantity || 1,
                    name: productName,
                    option_id: optionId,
                    source_product_id: sourceProductId,
                    treatment_type_id: treatmentTypeId,
                    treatment_type_key: treatmentTypeKey,
                    question_id: questionId,
                  });
                  console.log('✅ Found product_selection (string):', {
                    response,
                    productId,
                    productName,
                    quantity,
                    optionId,
                    treatmentTypeId,
                    treatmentTypeKey,
                  });
                }
              }
            } else if (question.question_type === 'medication_dose_selector' && response) {
              // Handle medication dose selector responses
              try {
                const medicationData =
                  typeof response === "string" ? JSON.parse(response) : response;
                const productId =
                  medicationData?.product_context?.product_id || medicationData?.product_id;
                if (productId) {
                  selectedMedicines.push({
                    id: productId,
                    quantity: medicationData?.quantity || 1
                  });
                  console.log('✅ Found medication_dose_selector:', medicationData);
                }
              } catch (e) {
                console.warn('Failed to parse medication selector response:', response);
              }
            } else if (question.question_type === 'checkout' && response) {
              // Handle checkout question responses
              if (typeof response === 'object' && response.product_id) {
                selectedMedicines.push({
                  id: response.product_id,
                  quantity: 1
                });
                console.log('✅ Found checkout question:', response);
              }
            }
          });

          console.log('🎯 Selected medicines (before price resolution):', selectedMedicines);

          if (selectedMedicines.length === 0) {
            throw new Error('No product selection was found. Please select a treatment product before continuing.');
          }

          // Step 2.5: Resolve product prices using the API
          console.log('💰 Resolving product prices...');
          const productIds = selectedMedicines.map(med => med.id);
          const resolvedProducts = await api.resolveProducts(productIds);

          // Update selected medicines with actual prices
          const medicinesWithPrices = selectedMedicines.map(medicine => {
            const resolvedProduct = resolvedProducts.find(p => p.product_id === medicine.id);
            if (resolvedProduct) {
              return {
                ...medicine,
                price: parseFloat(resolvedProduct.final_price || resolvedProduct.price || 0),
                name: medicine.name || resolvedProduct.product_name
              };
            }
            // Fallback if product not resolved
            return {
              ...medicine,
              price: 0
            };
          });

          console.log('💰 Final selected medicines with prices:', medicinesWithPrices);

          // Step 3: Create order (include referral/coupon so checkout gets discount)
          let couponCode = (sessionStorage.getItem('welliemd_coupon_code') || '').trim();
          let referralCampaign = (sessionStorage.getItem('welliemd_referral_campaign') || '').trim();
          if (!couponCode && typeof window !== 'undefined' && window.location?.search) {
            const params = new URLSearchParams(window.location.search);
            const fromUrl = (params.get('promo') || params.get('coupon') || '').trim(); // promo= from Coupon modal
            const refFromUrl = (params.get('referral-campaign') || '').trim();
            if (fromUrl) couponCode = fromUrl;
            if (refFromUrl) referralCampaign = refFromUrl;
            if (couponCode || referralCampaign) {
              console.log('🎟️ Using promo/referral from URL at order create:', { couponCode, referralCampaign });
            }
          }
          if (currentToken.current) {
            const hasFileUploads = questionnaire.questions.some(
              (question) => question.question_type === "file_upload" && latestResponses[question.id]
            );
            if (hasFileUploads) {
              try {
                await queueDraftSave(
                  () => api.saveQuestionnaireDraft(
                    currentToken.current,
                    latestResponses,
                    currentQuestionIndex,
                    questionnaire.version,
                  ),
                );
                console.log("✅ Flushed latest draft before order create");
              } catch (draftFlushError) {
                console.warn("⚠️ Draft flush before order create failed:", draftFlushError);
              }
            }
          }
          console.log('🚀 Step 3: Creating order with medicines:', medicinesWithPrices, couponCode ? { couponCode, referralCampaign } : '');
          const followupSessionId =
            sessionStorage.getItem('followup_session_id') ||
            sessionStorage.getItem('existing_patient_session_id') ||
            null;
          const orderType = followupSessionId ? 'follow_up' : 'onboarding';
          const orderResult = await api.createOrder(
            resolvedPatientId,
            orderQuestionnaireAnswers,
            medicinesWithPrices,
            orderType,
            questionnaireSessionIdRef.current || undefined,
            couponCode || null,
            referralCampaign || null,
            followupSessionId
          );

          console.log('✅ Order created:', orderResult);

          // Return the checkout URL for redirection
          setCompleted(true);
          return {
            success: true,
            checkout_url: orderResult.checkout_url,
            checkout_token: orderResult.checkout_token,
            order_id: orderResult.order_id,
            visit_id: orderResult.visit_id,
            patient_id: resolvedPatientId,
            is_new_patient: finalPatientInfo.is_new_patient
          };

        } catch (checkoutError) {
          console.error('❌ New checkout flow failed:', checkoutError);
          console.error('❌ Error details:', {
            message: checkoutError.message,
            response: checkoutError.response?.data,
            status: checkoutError.response?.status
          });
          setError(getApiErrorMessage(checkoutError));
          throw checkoutError;
        }
      } catch (err) {
        const checkout = treatmentCheckoutRef.current;
        const idempotencyKey = treatmentCheckoutAttemptRef.current;
        const recovery = checkoutRecoveryForError(err);
        if (recovery && checkout && idempotencyKey) {
          setTreatmentCheckoutRecovery(recovery);
          persistTreatmentCheckoutResume(checkout, {
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
            idempotencyKey,
            recovery,
          });
        }
        setError(getApiErrorMessage(err) || "Failed to complete questionnaire");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [
      questionnaire,
      responses,
      navigationHistory,
      sessionStartTime,
      calculatedBMI,
      isPreviewMode,
      journeyBoundary,
      previewProgramId,
      previewCustomProgramId,
      previewCapabilityToken,
      runtimeProgramId,
      runtimeCustomProgramId,
      runtimeSourceId,
      runtimeApiBaseUrl,
      queueDraftSave,
    ]
  );

  const recoverTreatmentCheckoutAction = useCallback(
    async (
      action,
      treatmentTypeId = null,
      optionId = null,
      recoveryCouponCode = undefined,
    ) => {
      const checkout = treatmentCheckoutRef.current;
      const idempotencyKey = treatmentCheckoutAttemptRef.current;
      if (!checkout?.checkout_capability || !idempotencyKey) {
        throw new Error("Checkout recovery is no longer available.");
      }
      setLoading(true);
      setTreatmentRecoveryMessage("");
      try {
        const result = await recoverTreatmentCheckout({
          checkoutCapability: checkout.checkout_capability,
          runtimeSessionRevision: checkout.session_revision,
          idempotencyKey,
          recoveryAction: action,
          treatmentTypeId,
          optionId,
          couponCode: recoveryCouponCode,
          apiBaseUrl: checkout.runtime_api_base_url || runtimeApiBaseUrl,
        });
        if (action === TREATMENT_RECOVERY_ACTION.updatePayment) {
          setTreatmentCheckoutRecovery(null);
          persistTreatmentCheckoutResume(checkout, {
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
            idempotencyKey,
            recovery: null,
          });
          setError(null);
          setTreatmentRecoveryMessage(
            "Payment details can now be updated and submitted again."
          );
          return result;
        }
        if (action === TREATMENT_RECOVERY_ACTION.saveResume) {
          persistTreatmentCheckoutResume(result.session || checkout, {
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
            sessionToken: result.resume?.session_token,
            expiresAt: result.resume?.expires_at,
            idempotencyKey,
            recovery: result.attempt,
          });
          setTreatmentRecoveryMessage(
            "Your progress is saved. You can return to this checkout safely."
          );
          return result;
        }
        if (action === TREATMENT_RECOVERY_ACTION.support) {
          persistTreatmentCheckoutResume(checkout, {
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
            idempotencyKey,
            recovery: result.attempt || treatmentCheckoutRecovery,
          });
          setTreatmentRecoveryMessage(
            "Your checkout reference is saved for the support team."
          );
          return result;
        }
        if (result.checkout_cancelled) {
          clearTreatmentAuthority();
          setTreatmentCheckoutRecovery(null);
          setCompleted(true);
          return result;
        }

        const resumed = result.session;
        if (result.requires_quote_confirmation && resumed?.quote_change) {
          setTreatmentAuthorityRef(resumed);
          treatmentCheckoutRef.current = resumed;
          treatmentCheckoutAttemptRef.current = null;
          setTreatmentCheckout(resumed);
          persistTreatmentAuthority(resumed, {
            sourceKind: runtimeSourceKind,
            sourceId: runtimeSourceId,
          });
          setTreatmentCheckoutRecovery(null);
          setTreatmentQuoteChange(resumed.quote_change);
          setError(null);
          return result;
        }
        setTreatmentAuthorityRef(resumed);
        persistTreatmentAuthority(resumed, {
          sourceKind: runtimeSourceKind,
          sourceId: runtimeSourceId,
        });
        treatmentCheckoutRef.current = null;
        treatmentCheckoutAttemptRef.current = null;
        setTreatmentCheckout(null);
        setTreatmentCheckoutRecovery(null);
        setError(null);
        const questions = computeVisibleQuestions(responsesRef.current);
        const targetIndex = questions.findIndex((question) => (
          resumed?.state === "product_selection"
            ? question.question_type === "product_selection"
            : (
              question.question_type === "matched_program_selection"
              && question.matching_config?.stage === "potential"
            )
        ));
        if (targetIndex < 0) {
          throw new Error("The recovery step is unavailable.");
        }
        setCurrentQuestionIndex(targetIndex);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [
      computeVisibleQuestions,
      runtimeSourceId,
      runtimeSourceKind,
      runtimeApiBaseUrl,
    ]
  );

  const confirmTreatmentQuoteChange = useCallback(() => {
    setTreatmentQuoteChange(null);
    setTreatmentRecoveryMessage(
      "Updated quote confirmed. Submit payment when ready."
    );
  }, []);

  const getProgress = useCallback(() => {
    const visibleQuestions = getVisibleQuestions();
    if (!visibleQuestions.length) return 0;

    const answered = visibleQuestions.filter((q) => {
      const response = responses[q.id];
      return response !== undefined && response !== "" && response !== null;
    });

    return Math.round((answered.length / visibleQuestions.length) * 100);
  }, [responses, getVisibleQuestions]);

  const getQuestionValidationError = useCallback(
    (questionId) => {
      return validationErrors[questionId]?.[0] || null;
    },
    [validationErrors]
  );

  const isResponseValid = useCallback(
    (question, responseValue) => {
      const errors = validateResponse(question, responseValue, false);
      return errors.length === 0;
    },
    [validateResponse]
  );

  const handleSetPatientInfo = useCallback((patientData) => {
    setPatientInfo(patientData);
  }, []);

  const clearDisqualification = useCallback(
    (questionIdToClear) => {
      setDisqualified(false);
      setDisqualificationReason(null);

      // Clear the response for the disqualifying question
      if (questionIdToClear) {
        const newResponses = { ...responsesRef.current };
        delete newResponses[questionIdToClear];
        responsesRef.current = newResponses;
        setResponses(newResponses);
      }
    },
    []
  );

  // --- DRAFT SANITIZATION ---
  // Strip large base64 fileData blobs from responses before saving to draft storage/API.
  // This prevents repeated multi-MB payload uploads during autosave, which causes
  // mobile network contention and 50-70s checkout load times.
  // Local responses state remains UNCHANGED — user still sees image previews.
  // Helper to instantly flush draft state to storage
  const flushDraftToStorage = useCallback(async (customResponses = null, customIndex = null) => {
    if (!questionnaire || !hasRehydrated.current || disableLocalDraft) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    const targetResponses = customResponses || responsesRef.current || responses;
    const targetIndex = typeof customIndex === "number" ? customIndex : currentQuestionIndex;
    const sanitizedResponses = sanitizeDraftResponses(targetResponses);

    const draft = {
      templateId,
      templateVersion: questionnaire.version,
      ...(hasTreatmentAuthority && !isPreviewMode
        ? {
            runtime_identity: treatmentRuntimeDraftIdentity({
              session: treatmentAuthorityRef.current,
              sourceKind: runtimeSourceKind,
              sourceId: runtimeSourceId,
              apiBaseUrl: runtimeApiBaseUrl,
            }),
          }
        : {}),
      answers: targetResponses,
      current_question_index: targetIndex,
      updatedAt: Date.now(),
      patientInfo: patientInfo
        ? {
          patient_id: patientInfo.patient_id,
          email: patientInfo.email,
          phone: patientInfo.phone,
          is_new_patient: patientInfo.is_new_patient,
        }
        : null,
    };

    if (!disableLocalDraft) {
      await saveDraftToStorage(activeDraftKeyRef.current, draft);
    }

    if (currentToken.current) {
      try {
        await queueDraftSave(
          () => api.saveQuestionnaireDraft(
            currentToken.current,
            sanitizedResponses,
            targetIndex,
            questionnaire.version,
          ),
        );
      } catch (err) {
        console.error("Draft API save failed:", err);
        if (err.response?.status === 409) {
          removeDraftFromStorage(activeDraftKeyRef.current);
        }
      }
    }
  }, [
    questionnaire,
    disableLocalDraft,
    responses,
    currentQuestionIndex,
    templateId,
    hasTreatmentAuthority,
    isPreviewMode,
    runtimeSourceKind,
    runtimeSourceId,
    runtimeApiBaseUrl,
    patientInfo,
    queueDraftSave,
  ]);

  flushDraftToStorageRef.current = flushDraftToStorage;

  // --- PERSISTENCE EFFECT ---
  // Debounce draft saving whenever answers or navigation changes
  useEffect(() => {
    if (!questionnaire || !hasRehydrated.current) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(() => {
      flushDraftToStorage();
    }, 500); // 500ms Debounce (fast client-side persistence)

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [flushDraftToStorage, responses, currentQuestionIndex, questionnaire]);

  // Cleanup on success
  useEffect(() => {
    if (completed) {
      removeDraftFromStorage(activeDraftKeyRef.current);
      if (hasTreatmentAuthority) removeDraftFromStorage(draftKey);
      if (runtimeSourceId) clearTreatmentAuthority();
    }
  }, [completed, draftKey, runtimeSourceId, hasTreatmentAuthority]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (weightDebounceTimer.current) {
        clearTimeout(weightDebounceTimer.current);
      }
      if (dobDebounceTimer.current) {
        clearTimeout(dobDebounceTimer.current);
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const triggerDisqualification = useCallback((reason, questionText, questionId, condition) => {
    setDisqualified(true);
    setDisqualificationReason({
      disqualified: true,
      reason: reason,
      questionText: questionText || "Eligibility Check",
      disqualifyingCondition: condition || 'manual',
      questionId: questionId,
    });
  }, []);

  return {
    questionnaire,
    responses,
    currentQuestionIndex,
    loading,
    isRehydrating,
    error,
    treatmentRuntimeError,
    completed,
    disqualified,
    disqualificationReason,
    calculatedBMI,

    currentQuestion: getCurrentQuestion(),
    visibleQuestions: getVisibleQuestions(),
    isLastQuestion: isLastQuestion(),
    progress: getProgress(),

    getQuestionValidationError,
    isResponseValid,

    updateResponse,
    wasLastResponseDisqualified,
    batchUpdateResponses,
    getResponsesSnapshot,
    clearQuestionResponses,
    resetTreatmentPatientState,
    nextQuestion,
    navigateToTreatmentAuthorityState,
    navigateToCurrentTreatmentAuthority,
    navigateToStableQuestion,
    previousQuestion,
    setCurrentQuestionIndex,
    updateTreatmentPreviewProfile,
    validateResponse,
    checkEarlyDisqualification,
    clearDisqualification,
    triggerDisqualification,
    complete,
    initialize,
    patientInfo,
    setPatientInfo: handleSetPatientInfo,
    questionnaireBranding,
    treatmentAuthority,
    treatmentCheckout,
    treatmentCheckoutRecovery,
    treatmentRecoveryMessage,
    treatmentQuoteChange,
    confirmTreatmentQuoteChange,
    recoverTreatmentCheckoutAction,
    synchronizeTreatmentAuthority,
    continueTreatmentWithEligiblePrograms,
    validateTreatmentLocation,
    checkpointTreatmentProgramAnswer,
    bindTreatmentPatientAuthority,
    resetTreatmentPreviewAuthority,
    resetTreatmentPatientAuthority,
    rewindTreatmentResponses,
    removeQuestionnaireFile,
    uploadTreatmentRuntimeFile,
  };
};
